import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { MapPin, ArrowUpDown, Eye, BarChart3, ChevronDown, Home, CalendarCheck, Wallet, Star, Pencil, MoreVertical, Plus, Search, SlidersHorizontal, TrendingUp, MessageSquare, Settings, HelpCircle, FileText, Calendar as CalendarIcon, Bell, Users, Check, X, ChevronLeft, ChevronRight, Clock, Trash2, ShieldCheck, ShieldAlert, CreditCard, ArrowDownRight, ArrowUpRight, Download, Building2, Minus, CircleCheck, CircleAlert, Bed, Bath, CalendarDays, Send, FileImage, Mail, MessageCircle, LogOut, LayoutDashboard, Share2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/layout/Header";
import beraLogo from "@/assets/berastays-logo.png";
import PropertyCard from "@/components/explore/PropertyCard";
import PropertyFilters, { FilterValues } from "@/components/explore/PropertyFilters";
import PropertyCardSkeleton from "@/components/explore/PropertyCardSkeleton";
import { InfiniteScrollLoader } from "@/components/explore/InfiniteScrollLoader";
import { Pagination } from "@/components/explore/Pagination";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { SEOHead } from "@/components/seo/SEOHead";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useAuth } from "@/contexts/AuthContext";
import { CompactSearchBar } from "@/components/search/CompactSearchBar";
import { MobileSearchBar } from "@/components/search/MobileSearchBar";
import { DesktopSearchBar } from "@/components/search/DesktopSearchBar";
import { ANGOLA_PROVINCES } from "@/data/angola-locations";
import {
  getBeraOpportunitySortedProperties,
  BeraOpportunityConfig,
  DEFAULT_CONFIG,
} from "@/lib/beraOpportunity";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import EditPropertyDialog from "@/components/property/EditPropertyDialog";
import ShareSpaceDialog from "@/components/property/ShareSpaceDialog";
import { getAmenityIcon, getAmenityLabel } from "@/lib/amenityIcons";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { cn, getFirstImageUrl, getSafeImageUrls, formatKz } from "@/lib/utils";
import { isProfileKycVerified } from "@/lib/kycStatus";
import { useFmt } from "@/hooks/useFmt";
import { Textarea } from "@/components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import { PROPERTY_TYPE_VALUES } from "@/lib/propertyTypes";
import { insuranceTables } from "@/lib/insuranceClient";
import { InsuredBadge } from "@/components/VerifiedBadge";
import {
  STATUS_LABEL,
  STATUS_COLOR,
  REQUIRED_CATEGORIES,
  DEFAULT_COVERAGE_INCLUDED,
  DEFAULT_COVERAGE_EXCLUDED,
  SUPPORT,
  buildMediaCount,
  deriveOwnerStatus,
  formatDate,
  daysUntil,
  type InsuranceStatus,
  type MediaCategory,
} from "@/lib/insurance";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";
import { downloadReceiptPDF, type ReceiptData } from "@/lib/receiptUtils";
import { exportToCSV, exportToPDF } from "@/lib/exportUtils";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as ChartTooltip } from "recharts";
import { Calendar } from "@/components/ui/calendar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import BeraAIChat from "@/components/bera-ai/BeraAIChat";
import { HostSettings } from "@/components/profile/HostSettings";
import { HostProfile } from "@/components/profile/HostProfile";
import NotificationBell from "@/components/notifications/NotificationBell";
import { CoralPin } from "@/components/branding/CoralPin";

import { tAuto } from "@/lib/tAuto";
type SortOption = "relevance" | "price_asc" | "price_desc" | "rating" | "newest" | "bera_opportunity";

const DEFAULT_FILTERS: FilterValues = {
  search: "",
  province: "",
  propertyType: "",
  priceRange: [0, 500000],
  bedrooms: 0,
  bathrooms: 0,
  beds: 0,
  amenities: [],
  features: [],
  safety: [],
  highlights: [],
  propertyCategory: [],
  hostLanguage: [],
  accommodationType: "",
};

const ITEMS_PER_PAGE = 20;
const ITEMS_PER_MOBILE_PAGE = 10;

const Explorar = () => {
  const fmt = useFmt();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || searchParams.get("search") || "";
  const typeParam = searchParams.get("type") || "";
  const locationParam = searchParams.get("location") || "";
  const checkInParam = searchParams.get("checkIn") || "";
  const checkOutParam = searchParams.get("checkOut") || "";
  const guestsParam = searchParams.get("guests") || "";
  const adultsParam = searchParams.get("adults") || "";
  const childrenParam = searchParams.get("children") || "";
  const categoryParam = searchParams.get("category") || "";
  const provinceParam = searchParams.get("province") || "";

  const [properties, setProperties] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>("bera_opportunity");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isHost, isGuest, isSuperAdmin, loading: rolesLoading } = useUserRoles();
  const { user, signOut, loading: authLoading } = useAuth();
  
  // Determine if current user is a host-only (not guest, not super admin)
  const isHostOnly = isHost && !isGuest && !isSuperAdmin;
  // Gate to avoid a flash of the guest view while auth/roles are still resolving
  const isRoleResolving = authLoading || (!!user?.id && rolesLoading);

  const now0 = new Date();
  const [selectedPeriod, setSelectedPeriod] = useState<string>(
    `${now0.getFullYear()}-${String(now0.getMonth() + 1).padStart(2, "0")}`
  );
  const [insuranceSearch, setInsuranceSearch] = useState("");
  const [insuranceFilter, setInsuranceFilter] = useState<string>("all");

  const periodOptions = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based
    const formatMonth = (d: Date) => {
      return d.toLocaleDateString("pt-AO", { month: "long" }).replace(/^\w/, (c) => c.toUpperCase());
    };
    const opts: { value: string; label: string }[] = [];
    for (let m = 0; m <= currentMonth; m++) {
      const d = new Date(year, m, 1);
      const value = `${year}-${String(m + 1).padStart(2, "0")}`;
      const isCurrent = m === currentMonth;
      opts.push({ value, label: isCurrent ? `${formatMonth(d)} (este mês)` : formatMonth(d) });
    }
    return opts.reverse(); // most recent first
  }, []);

  // Host queries (only run if isHostOnly is true)
  const { data: hostProperties, isLoading: hostPropsLoading, refetch: refetchHostProperties } = useQuery({
    queryKey: ["host-properties-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("owner_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly,
  });

  const { data: hostBookings = [], isLoading: hostBookingsLoading, refetch: refetchHostBookings } = useQuery({
    queryKey: ["host-bookings-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select(`
          *, 
          properties:property_id (
            title, 
            images, 
            booking_type,
            price_per_night,
            city,
            province
          ), 
          guests:guest_id (
            full_name, 
            avatar_url, 
            phone, 
            email
          )
        `)
        .eq("host_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly,
  });

  const { data: hostReviews = [], isLoading: hostReviewsLoading, refetch: refetchHostReviews } = useQuery({
    queryKey: ["host-reviews-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("host_id", user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly,
  });

  const { data: hostBalanceData } = useQuery({
    queryKey: ["host-balance-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("host_balances")
        .select("*")
        .eq("host_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && isHostOnly,
  });

  const { data: hostPaymentHistory = [], isLoading: hostPaymentsLoading } = useQuery({
    queryKey: ["host-payment-history-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select("id, property_id, status, payment_status, host_payout, currency, check_in, check_out, created_at, updated_at, properties:property_id (title), guests:guest_id (full_name)")
        .eq("host_id", user.id)
        .in("payment_status", ["paid", "refunded"])
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly,
  });

  const { data: hostWithdrawals = [], isLoading: hostWithdrawalsLoading, refetch: refetchHostWithdrawals } = useQuery({
    queryKey: ["host-withdrawals-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("host_withdrawals")
        .select("*")
        .eq("host_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly,
  });

  // Real analytics: property views & clicks across all host properties for current year
  const hostPropertyIds = useMemo(
    () => (hostProperties || []).map((p: any) => p.id).filter(Boolean),
    [hostProperties]
  );

  const { data: hostViewsYear = [] } = useQuery({
    queryKey: ["host-views-year", user?.id, hostPropertyIds.join(",")],
    queryFn: async () => {
      if (!hostPropertyIds.length) return [];
      const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();
      const { data, error } = await (supabase as any)
        .from("property_views")
        .select("property_id, viewed_at")
        .in("property_id", hostPropertyIds)
        .gte("viewed_at", startOfYear);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly && hostPropertyIds.length > 0,
    staleTime: 60_000,
  });

  const { data: hostClicksYear = [] } = useQuery({
    queryKey: ["host-clicks-year", user?.id, hostPropertyIds.join(",")],
    queryFn: async () => {
      if (!hostPropertyIds.length) return [];
      const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();
      const { data, error } = await (supabase as any)
        .from("property_clicks")
        .select("property_id, clicked_at")
        .in("property_id", hostPropertyIds)
        .gte("clicked_at", startOfYear);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly && hostPropertyIds.length > 0,
    staleTime: 60_000,
  });

  const { data: companyInfo } = useQuery({
    queryKey: ["company-info-explore"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("value")
        .eq("key", "company_info")
        .maybeSingle();
      if (error) throw error;
      return data?.value as { name: string; nif: string; address: string; phone: string; email: string; ambassador_message: string } || { name: "BeraStays", nif: "5002702908", address: "Luanda, Talatona", phone: "", email: "", ambassador_message: "" };
    },
  });

  const { data: unreadMessagesCount = 0 } = useQuery({
    queryKey: ["host-unread-messages-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count, error } = await supabase
        .from("chat_messages")
        .select("*", { count: "exact", head: true })
        .eq("receiver_id", user.id)
        .eq("is_read", false);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id && isHostOnly,
  });

  const { data: profile } = useQuery({
    queryKey: ["user-profile-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("is_verified, is_admin_verified, admin_bypass_kyc, verification_status, iban, iban_confirmed_at, full_name, avatar_url")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && isHostOnly,
  });

  const isVerified = isProfileKycVerified(profile, { isPrivilegedAdmin: isSuperAdmin });

  const { data: hostOfferNotifications = [], isLoading: hostOffersLoading, refetch: refetchHostOffers } = useQuery({
    queryKey: ["host-offer-notifications-explore", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await (supabase as any)
        .from("push_notifications")
        .select(`
          id,
          is_read,
          created_at,
          offer:offer_id (
            id,
            guest_id,
            property_type,
            province,
            city,
            location_description,
            check_in,
            check_out,
            max_price_per_night,
            bedrooms,
            bathrooms,
            nights,
            amenities,
            notes,
            expires_at,
            status,
            guest:guest_id (
              full_name,
              avatar_url
            )
          )
        `)
        .eq("user_id", user.id)
        .eq("type", "offer_broadcast")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isHostOnly,
  });

  const { data: hostInsuranceRequests = [], refetch: refetchHostInsurance, isLoading: isInsuranceLoading } = useQuery({
    queryKey: ["host-insurance-requests-explore", user?.id],
    enabled: !!user?.id && isHostOnly,
    queryFn: async () => {
      const { data, error } = await insuranceTables.requests()
        .select(`
          id, property_id, status, insurance_type, premium_amount,
          start_date, end_date, policy_number, requested_at, approved_at, activated_at,
          property:properties!insurance_requests_property_id_fkey (
            id, title, property_type, city, province, address, images, description,
            bedrooms, bathrooms, price_per_night, amenities, status, insurance_badge_active
          ),
          company:insurance_companies!insurance_requests_company_id_fkey (
            id, name, logo_url
          )
        `)
        .eq("owner_id", user!.id)
        .order("requested_at", { ascending: false });

      if (error) throw error;

      // Fetch media count grouped per request
      const requestIds = (data || []).map((r: any) => r.id);
      const mediaByRequest: Record<string, { category: MediaCategory; media_type: "image" | "video" }[]> = {};
      if (requestIds.length) {
        const { data: media } = await insuranceTables.media()
          .select("request_id, category, media_type")
          .in("request_id", requestIds);
        for (const m of (media as any[]) || []) {
          if (!mediaByRequest[m.request_id]) mediaByRequest[m.request_id] = [];
          mediaByRequest[m.request_id].push({ category: m.category, media_type: m.media_type });
        }
      }

      return ((data as any[]) || []).map((r) => ({
        ...r,
        media_count: mediaByRequest[r.id] || [],
      })) as any[];
    },
  });

  // Realtime subscription for Explorar (owner dashboard)
  useEffect(() => {
    if (!user?.id || !isHostOnly) return;

    const propertiesChannel = supabase
      .channel("explorar-properties-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "properties",
          filter: `owner_id=eq.${user.id}`,
        },
        () => {
          refetchHostProperties();
        }
      )
      .subscribe();

    const bookingsChannel = supabase
      .channel("explorar-bookings-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "bookings",
          filter: `host_id=eq.${user.id}`,
        },
        () => {
          refetchHostBookings();
        }
      )
      .subscribe();

    const reviewsChannel = supabase
      .channel("explorar-reviews-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "reviews",
          filter: `host_id=eq.${user.id}`,
        },
        () => {
          refetchHostReviews();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(propertiesChannel);
      supabase.removeChannel(bookingsChannel);
      supabase.removeChannel(reviewsChannel);
    };
  }, [user?.id, isHostOnly, refetchHostProperties, refetchHostBookings, refetchHostReviews]);

  // Active menu/tab for Host Dashboard on desktop
  const [activeMenu, setActiveMenu] = useState("dashboard");

  // Permite que notificações e outras rotas abram secções dentro do Explorar
  // através de ?menu=reservas|mensagens|pedidos_oferta|configuracoes|...&booking=<id>&offer=<id>&chat=<bookingId>
  useEffect(() => {
    if (!isHostOnly) return;
    const menu = searchParams.get("menu");
    const allowed = new Set([
      "dashboard", "imoveis", "reservas", "mensagens", "financeiro",
      "desempenho", "avaliacoes", "pedidos_oferta", "seguro",
      "configuracoes", "perfil",
    ]);
    if (menu && allowed.has(menu)) {
      setActiveMenu(menu);
    }
    const bookingHint = searchParams.get("booking");
    if (bookingHint) {
      // Pequeno scroll para a secção de reservas após o render
      setTimeout(() => {
        const el = document.getElementById(`host-booking-${bookingHint}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 250);
    }
  }, [searchParams, isHostOnly]);

  // State for search input inside host dashboard
  const [hostSearchQuery, setHostSearchQuery] = useState("");
  // Edit property state
  const [editProperty, setEditProperty] = useState<any>(null);

  // Deletion and action states
  const [deletePropertyId, setDeletePropertyId] = useState<string | null>(null);
  const [shareProperty, setShareProperty] = useState<{ id: string; title: string } | null>(null);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Withdrawal states
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Booking action/deletion states
  const [deleteBookingId, setDeleteBookingId] = useState<string | null>(null);
  const [isDeletingBooking, setIsDeletingBooking] = useState(false);

  // Pagination and filtering states
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [reviewNotification, setReviewNotification] = useState<any>(null);
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);

  // Auto-open offer review if offer query param is present
  const offerIdParam = searchParams.get("offer");
  useEffect(() => {
    if (offerIdParam && hostOfferNotifications && hostOfferNotifications.length > 0) {
      const targetNotif = hostOfferNotifications.find(
        (n: any) => n.offer?.id === offerIdParam || n.id === offerIdParam || n.offer_id === offerIdParam
      );
      if (targetNotif) {
        setReviewNotification(targetNotif);
      }
    }
  }, [offerIdParam, hostOfferNotifications]);

  // BeraOpportunity Config (can be loaded from admin settings)
  const [beraConfig, setBeraConfig] = useState<BeraOpportunityConfig>(DEFAULT_CONFIG);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedProperties, setDisplayedProperties] = useState<any[]>([]);

  // Parse total guests from adults + children or guests param
  const totalGuests = useMemo(() => {
    if (adultsParam || childrenParam) {
      return (parseInt(adultsParam || "0", 10) + parseInt(childrenParam || "0", 10)) || 0;
    }
    return parseInt(guestsParam || "0", 10) || 0;
  }, [adultsParam, childrenParam, guestsParam]);

  // Resolve province ID to name for DB matching
  const provinceNameFromId = useMemo(() => {
    if (!provinceParam || provinceParam === "all") return "";
    const p = ANGOLA_PROVINCES.find((p) => p.id === provinceParam);
    return p?.name || provinceParam;
  }, [provinceParam]);

  const [filters, setFilters] = useState<FilterValues>({
    ...DEFAULT_FILTERS,
    search: searchQuery || locationParam,
    propertyType: typeParam || categoryParam,
    province: provinceNameFromId,
  });

  // Load BeraOpportunity config from admin settings
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data } = await (supabase as any)
          .from("system_settings")
          .select("value")
          .eq("key", "bera_opportunity_config")
          .single();

        if (data?.value) {
          setBeraConfig({ ...DEFAULT_CONFIG, ...(data.value as any) });
        }
      } catch {
        // Use default config if not available
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: searchQuery || locationParam,
      propertyType: typeParam || categoryParam || prev.propertyType,
      province: provinceNameFromId,
    }));
  }, [searchQuery, typeParam, locationParam, categoryParam, provinceNameFromId]);

  useEffect(() => {
    fetchProperties();
  }, []);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy]);

  const fetchProperties = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("status", "active")
        .eq("is_verified", true);

      if (error) throw error;

      const parsedProperties =
        data?.map((prop) => ({
          ...prop,
          images:
            typeof prop.images === "string"
              ? JSON.parse(prop.images)
              : prop.images || [],
        })) || [];

      setProperties(parsedProperties);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters
  const filteredProperties = useMemo(() => {
    let result = [...properties];

    // Search filter - includes location/address/city/province/title
    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(
        (p) =>
          p.title?.toLowerCase().includes(s) ||
          p.city?.toLowerCase().includes(s) ||
          p.province?.toLowerCase().includes(s) ||
          p.address?.toLowerCase().includes(s)
      );
    }

    // Location parameter specific filter (when location is provided separately)
    if (locationParam) {
      const loc = locationParam.toLowerCase();
      result = result.filter(
        (p) =>
          p.city?.toLowerCase().includes(loc) ||
          p.province?.toLowerCase().includes(loc) ||
          p.address?.toLowerCase().includes(loc)
      );
    }

    if (filters.province) {
      result = result.filter((p) => p.province === filters.province);
    }

    // "all" significa todos os tipos — não aplicar filtro
    if (filters.propertyType && filters.propertyType !== "all") {
      result = result.filter(
        (p) =>
          p.property_type?.toLowerCase() === filters.propertyType.toLowerCase()
      );
    }

    if (filters.propertyCategory.length > 0) {
      result = result.filter((p) =>
        filters.propertyCategory.some(
          (cat) => p.property_type?.toLowerCase() === cat.toLowerCase()
        )
      );
    }

    // Filter by accommodation type (Tipo de acomodação: quarto/espaco_inteiro)
    if (filters.accommodationType) {
      result = result.filter((p) =>
        p.accommodation_type?.toLowerCase() === filters.accommodationType.toLowerCase()
      );
    }

    result = result.filter(
      (p) =>
        p.price_per_night >= filters.priceRange[0] &&
        p.price_per_night <= filters.priceRange[1]
    );

    if (filters.bedrooms > 0) {
      result = result.filter((p) =>
        filters.bedrooms === 5
          ? p.bedrooms >= 5
          : p.bedrooms >= filters.bedrooms
      );
    }

    if (filters.bathrooms > 0) {
      result = result.filter((p) =>
        filters.bathrooms === 5
          ? p.bathrooms >= 5
          : p.bathrooms >= filters.bathrooms
      );
    }

    // Filter by guests capacity from URL params
    if (totalGuests > 0) {
      result = result.filter((p) => {
        const maxGuests =
          p.max_guests ||
          p.max_adults + (p.max_children || 0) ||
          0;
        return maxGuests >= totalGuests;
      });
    }

    if (filters.amenities.length > 0) {
      result = result.filter((p) => {
        const propAmenities = Array.isArray(p.amenities) ? p.amenities : [];
        return filters.amenities.every((a) => propAmenities.includes(a));
      });
    }

    if (filters.features.length > 0) {
      result = result.filter((p) => {
        const propAmenities = Array.isArray(p.amenities) ? p.amenities : [];
        return filters.features.every((a) => propAmenities.includes(a));
      });
    }

    if (filters.highlights.includes("top_rated")) {
      result = result.filter((p) => (p.average_rating || 0) >= 4.5);
    }
    if (filters.highlights.includes("luxury")) {
      result = result.filter((p) => p.price_per_night >= 100000);
    }

    return result;
  }, [properties, filters, locationParam, totalGuests]);

  // Apply sorting and BeraOpportunity algorithm
  const sortedProperties = useMemo(() => {
    let result = [...filteredProperties];

    switch (sortBy) {
      case "price_asc":
        result.sort((a, b) => a.price_per_night - b.price_per_night);
        break;
      case "price_desc":
        result.sort((a, b) => b.price_per_night - a.price_per_night);
        break;
      case "rating":
        result.sort(
          (a, b) => (b.average_rating || 0) - (a.average_rating || 0)
        );
        break;
      case "newest":
        result.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
      case "bera_opportunity":
        // Apply BeraOpportunity algorithm for fair distribution
        result = getBeraOpportunitySortedProperties(result, beraConfig);
        break;
      default:
        // Default: combine rating with BeraOpportunity fairness
        result = getBeraOpportunitySortedProperties(result, beraConfig);
    }

    return result;
  }, [filteredProperties, sortBy, beraConfig]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    const itemsPerPage = isMobile ? ITEMS_PER_MOBILE_PAGE : ITEMS_PER_PAGE;
    return Math.ceil(sortedProperties.length / itemsPerPage);
  }, [sortedProperties.length, isMobile]);

  // Get current page items
  const currentItems = useMemo(() => {
    const itemsPerPage = isMobile ? ITEMS_PER_MOBILE_PAGE : ITEMS_PER_PAGE;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedProperties.slice(startIndex, endIndex);
  }, [sortedProperties, currentPage, isMobile]);

  // Infinite scroll handler
  const loadMore = useCallback(() => {
    if (isLoadingMore || currentPage >= totalPages) return;

    setIsLoadingMore(true);

    // Simulate loading delay for smooth UX
    setTimeout(() => {
      setCurrentPage((prev) => prev + 1);
      setIsLoadingMore(false);
    }, 300);
  }, [currentPage, totalPages, isLoadingMore]);

  // Setup infinite scroll
  const { loadMoreRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore: currentPage < totalPages,
    isLoading: isLoadingMore,
  });

  // Update displayed properties when page changes
  useEffect(() => {
    if (isMobile) {
      // Mobile: append for infinite scroll
      if (currentPage === 1) {
        setDisplayedProperties(currentItems);
      } else {
        setDisplayedProperties((prev) => {
          const newItems = currentItems.filter(
            (item) => !prev.some((p) => p.id === item.id)
          );
          return [...prev, ...newItems];
        });
      }
    } else {
      // Desktop: show only current page
      setDisplayedProperties(currentItems);
    }
  }, [currentItems, isMobile, currentPage]);

  // Host stats
  const hostOwnProperties = useMemo(() => {
    if (!isHostOnly || !user) return 0;
    return properties.filter((p) => p.owner_id === user.id).length;
  }, [properties, isHostOnly, user]);

  // Enriched insurance requests
  const enrichedInsurance = useMemo(() => {
    if (!isHostOnly) return [];
    return (hostInsuranceRequests || []).map((r: any) => {
      const count = buildMediaCount(r.media_count);
      const effectiveStatus = deriveOwnerStatus(r.status, count);
      return { ...r, count, effectiveStatus };
    });
  }, [hostInsuranceRequests, isHostOnly]);

  // Filtered insurance requests
  const filteredInsurance = useMemo(() => {
    if (!isHostOnly) return [];
    return enrichedInsurance.filter((r: any) => {
      if (insuranceFilter !== "all" && r.effectiveStatus !== insuranceFilter) return false;
      if (insuranceSearch) {
        const q = insuranceSearch.toLowerCase();
        const blob = `${r.property?.title || ""} ${r.property?.city || ""} ${r.property?.province || ""}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [enrichedInsurance, insuranceFilter, insuranceSearch, isHostOnly]);

  // Stats for insurance requests
  const insuranceStats = useMemo(() => {
    const totals = { active: 0, pending: 0, total: 0 };
    if (!isHostOnly) return totals;
    totals.total = enrichedInsurance.length;
    for (const r of enrichedInsurance) {
      if (r.effectiveStatus === "active") totals.active++;
      else if (["not_activated","documents_pending","ready_to_apply","submitted","in_review","docs_required"].includes(r.effectiveStatus)) totals.pending++;
    }
    return totals;
  }, [enrichedInsurance, isHostOnly]);

  const seoTitle = searchQuery
    ? `Aluguel de Casas e Apartamentos em ${searchQuery}`
    : "Explorar Imóveis em Angola";
  const seoDescription = searchQuery
    ? `Encontre casas e apartamentos para alugar em ${searchQuery} com Berastays.`
    : "Descubra casas, apartamentos e locais espetaculares para alugar em todas as províncias de Angola.";
  const seoKeywords = searchQuery
    ? `alugar casa ${searchQuery}, apartamentos ${searchQuery}, hospedagem ${searchQuery}`
    : "Airbnb Angola, aluguel de casas Angola, aluguel de apartamentos Angola";

  const handleDeleteProperty = async () => {
    if (!deletePropertyId) return;
    setIsDeleting(true);
    const { error } = await supabase.from("properties").delete().eq("id", deletePropertyId);
    setIsDeleting(false);
    setDeletePropertyId(null);
    if (error) {
      toast({ title: "Erro ao eliminar", description: tAuto("auto.pages_explorar.1", "Não foi possível eliminar o imóvel."), variant: "destructive" });
      return;
    }
    toast({ title: tAuto("auto.pages_explorar.2", "Imóvel eliminado"), description: tAuto("auto.pages_explorar.3", "O imóvel foi eliminado com sucesso.") });
    refetchHostProperties();
  };

  const handleDeleteAllProperties = async () => {
    if (!user?.id) return;
    setIsDeletingAll(true);
    const { error } = await supabase.from("properties").delete().eq("owner_id", user.id);
    setIsDeletingAll(false);
    setShowDeleteAllDialog(false);
    if (error) {
      toast({ title: "Erro ao limpar", description: tAuto("auto.pages_explorar.4", "Não foi possível remover os imóveis."), variant: "destructive" });
      return;
    }
    toast({ title: "Lista limpa", description: tAuto("auto.pages_explorar.5", "Todos os seus imóveis foram eliminados.") });
    refetchHostProperties();
  };

  const handleBookingAction = async (bookingId: string, action: "confirm" | "cancel" | "approve" | "reject") => {
    setActionLoading(bookingId);
    let newStatus: string;
    switch (action) {
      case "approve": newStatus = "awaiting_payment"; break;
      case "reject": newStatus = "cancelled"; break;
      case "confirm": newStatus = "confirmed"; break;
      case "cancel": newStatus = "cancelled"; break;
      default: newStatus = "cancelled";
    }

    const booking = hostBookings?.find((b: any) => b.id === bookingId);

    const { error } = await supabase.from("bookings").update({ status: newStatus }).eq("id", bookingId);
    setActionLoading(null);
    if (error) {
      toast({ title: "Erro", description: tAuto("auto.pages_explorar.6", "Não foi possível processar a ação."), variant: "destructive" });
      return;
    }

    if (action === "approve") {
      try {
        const notifData = [];
        if (booking?.guest_id) {
          notifData.push({
            user_id: booking.guest_id,
            title: tAuto("auto.pages_explorar.7", "Reserva Aprovada!"),
            message: `A sua reserva em ${booking?.properties?.title || "um imóvel"} foi aprovada. Por favor, efetue o pagamento para garantir a estadia.`,
            type: "booking_update",
            data: { booking_id: bookingId, status: "awaiting_payment" },
          });
        }
        if (booking?.affiliate_id) {
          notifData.push({
            user_id: booking.affiliate_id,
            title: tAuto("auto.pages_explorar.8", "Reserva Aprovada (Indicação)"),
            message: `Uma reserva feita através da sua indicação para ${booking?.properties?.title || "um imóvel"} foi aprovada e aguarda pagamento!`,
            type: "booking_update",
            data: { booking_id: bookingId, status: "awaiting_payment" },
          });
        }
        if (notifData.length > 0) {
          await supabase.from("push_notifications").insert(notifData);
        }
      } catch (err) {
        console.error("Failed to send approve notifications", err);
      }
    }

    if (action === "cancel" && booking?.payment_status === "paid" && booking?.guest_id) {
      try {
        await (supabase.rpc as any)("add_guest_refund_balance", {
          p_booking_id: booking.id,
          p_amount: booking.total_amount,
        });


        await supabase.from("refund_requests").insert({
          booking_id: bookingId,
          guest_id: booking.guest_id,
          host_id: user?.id,
          amount: booking.total_amount,
          currency: booking.currency || "AOA",
          reason: "Cancelamento pelo proprietário — reembolso automático 100%",
          policy_type: "host_cancelled",
          status: "approved",
        });

        if (hostBalanceData) {
          await supabase.from("host_balances").update({
            accounting_balance: Math.max(0, (hostBalanceData?.accounting_balance || 0) - booking.host_payout),
            updated_at: new Date().toISOString(),
          }).eq("host_id", user?.id);
        }

        await supabase.from("push_notifications").insert({
          user_id: booking.guest_id,
          title: tAuto("auto.pages_explorar.9", "Reserva Cancelada pelo Proprietário"),
          message: `O proprietário cancelou a sua reserva. ${booking.total_amount?.toLocaleString("pt-AO")} Kz foram creditados no seu Saldo de Reembolso.`,
          type: "refund_credited",
          data: { booking_id: bookingId, amount: booking.total_amount },
        });
      } catch (refundError) {
        console.error("Refund processing error:", refundError);
      }
    }

    const messages: Record<string, { title: string; desc: string }> = {
      approve: { title: tAuto("auto.pages_explorar.10", "Reserva aprovada"), desc: "O hóspede foi notificado para efetuar o pagamento." },
      reject: { title: tAuto("auto.pages_explorar.11", "Reserva rejeitada"), desc: "A reserva foi rejeitada." },
      confirm: { title: tAuto("auto.pages_explorar.12", "Reserva confirmada"), desc: "A reserva foi confirmada com sucesso." },
      cancel: { title: tAuto("auto.pages_explorar.13", "Reserva cancelada"), desc: booking?.payment_status === "paid" ? "O hóspede foi reembolsado a 100% no saldo de reembolso." : "A reserva foi cancelada." },
    };
    toast({ title: messages[action].title, description: messages[action].desc });
    refetchHostBookings();
  };

  const handleDeleteBooking = async () => {
    if (!deleteBookingId) return;

    const booking = hostBookings?.find((b: any) => b.id === deleteBookingId);
    if (booking?.payment_status === 'paid' && !['cancelled', 'completed'].includes(booking?.status)) {
      const checkOut = new Date(booking.check_out);
      if (checkOut >= new Date()) {
        toast({ title: tAuto("auto.pages_explorar.14", "Ação bloqueada"), description: tAuto("auto.pages_explorar.15", "Não é possível eliminar reservas pagas dentro do período de estadia."), variant: "destructive" });
        setDeleteBookingId(null);
        return;
      }
    }

    setIsDeletingBooking(true);

    try {
      await supabase.from("chat_messages").delete().eq("booking_id", deleteBookingId);
      await (supabase as any).from("push_notifications").delete().eq("data->>booking_id", deleteBookingId);

      const { error } = await supabase.from("bookings").delete().eq("id", deleteBookingId).eq("host_id", user?.id);

      if (error) throw error;

      toast({ title: tAuto("auto.pages_explorar.16", "Reserva eliminada"), description: tAuto("auto.pages_explorar.17", "A reserva foi permanentemente removida.") });
      refetchHostBookings();
    } catch (error: any) {
      toast({ title: "Erro ao eliminar", description: error.message, variant: "destructive" });
    } finally {
      setIsDeletingBooking(false);
      setDeleteBookingId(null);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 100000) {
      toast({ title: tAuto("auto.pages_explorar.18", "Valor inválido"), description: tAuto("auto.pages_explorar.19", "O valor mínimo de saque é 100.000 Kz."), variant: "destructive" });
      return;
    }
    const currentBalance = (hostBalanceData?.available_balance || 0) === 0 ? 70000 : (hostBalanceData?.available_balance || 0);
    if (amount > currentBalance) {
      toast({ title: "Saldo insuficiente", description: tAuto("auto.pages_explorar.20", "O valor excede o saldo disponível."), variant: "destructive" });
      return;
    }
    if (!profile?.iban) {
      toast({ title: tAuto("auto.pages_explorar.21", "IBAN não configurado"), description: tAuto("auto.pages_explorar.22", "Configure o seu IBAN nas definições antes de fazer um saque."), variant: "destructive" });
      setShowWithdrawDialog(false);
      setActiveMenu("configuracoes");
      return;
    }
    setWithdrawLoading(true);
    const { error } = await supabase.from("host_withdrawals").insert({
      host_id: user?.id,
      amount,
      currency: "Kz",
      iban: profile.iban,
      full_name: profile.full_name || "Proprietário",
      status: "processing"
    });
    if (!error) {
      await supabase.from("host_balances").update({
        available_balance: currentBalance - amount,
        updated_at: new Date().toISOString()
      }).eq("host_id", user?.id);
    }
    setWithdrawLoading(false);
    setShowWithdrawDialog(false);
    setWithdrawAmount("");
    if (error) {
      toast({ title: "Erro", description: tAuto("auto.pages_explorar.23", "Não foi possível processar o pedido."), variant: "destructive" });
      return;
    }
    toast({ title: tAuto("auto.pages_explorar.24", "Pedido de saque enviado"), description: tAuto("auto.pages_explorar.25", "O seu pedido de saque está a ser processado pela equipa financeira.") });
    refetchHostWithdrawals();
  };

  const handleAcceptOffer = async (notification: any) => {
    setIsSubmittingOffer(true);
    try {
      const offer = notification.offer;
      const { error: updateError } = await (supabase as any)
        .from("stay_offers")
        .update({ status: "accepted", accepted_by_host_id: user?.id, accepted_property_id: notification.property_id, accepted_price_per_night: offer.max_price_per_night, accepted_at: new Date().toISOString() })
        .eq("id", offer.id);

      if (updateError) throw updateError;

      const nights = offer.nights || 1;
      const subtotal = offer.max_price_per_night * nights;
      const { error: bookingError } = await (supabase as any).from("bookings").insert({
        property_id: notification.property_id,
        guest_id: offer.guest_id,
        host_id: user?.id,
        check_in: offer.check_in,
        check_out: offer.check_out,
        nights,
        price_per_night: offer.max_price_per_night,
        subtotal,
        service_fee: subtotal * 0.10,
        platform_fee: subtotal * 0.10,
        host_payout: subtotal * 0.90,
        status: "awaiting_payment",
        total_amount: subtotal,
        currency: "AOA",
      });

      if (bookingError) throw bookingError;

      await (supabase as any).from("push_notifications").update({ is_read: true }).eq("id", notification.id);

      toast({ title: "Oferta Aceite", description: tAuto("auto.pages_explorar.26", "A reserva foi criada e aguarda pagamento do hóspede.") });
      refetchHostOffers();
      refetchHostBookings();
    } catch (e: any) {
      toast({ title: "Erro", description: e.message || "Erro ao aceitar a oferta.", variant: "destructive" });
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const handleRejectOffer = async (notificationId: string) => {
    try {
      await (supabase as any).from("push_notifications").update({ is_read: true }).eq("id", notificationId);
      toast({ title: "Oferta Rejeitada", description: tAuto("auto.pages_explorar.27", "O pedido de oferta foi marcado como rejeitado.") });
      refetchHostOffers();
    } catch (e: any) {
      toast({ title: "Erro", description: "Erro ao rejeitar a oferta.", variant: "destructive" });
    }
  };

  const handleSendCounter = async (notification: any, counterData: any) => {
    setIsSubmittingOffer(true);
    try {
      const offer = notification.offer;
      const { error: counterError } = await (supabase as any)
        .from("offer_counters")
        .insert({
          offer_id: offer.id,
          host_id: user?.id,
          property_id: notification.property_id,
          proposed_price_per_night: counterData.price,
          message: counterData.message,
          status: "pending",
        });

      if (counterError) throw counterError;

      await (supabase as any).from("push_notifications").update({ is_read: true }).eq("id", notification.id);

      await (supabase as any).from("push_notifications").insert({
        user_id: offer.guest_id,
        type: "offer_counter",
        title: "Contraproposta Recebida",
        message: `O proprietário enviou uma contraproposta de ${counterData.price.toLocaleString("pt-AO")} Kz/noite para o seu pedido.`,
        data: { offer_id: offer.id, host_id: user?.id, notification_id: notification.id },
      });

      toast({ title: "Contraproposta enviada", description: tAuto("auto.pages_explorar.28", "O hóspede foi notificado da sua contraproposta.") });
      refetchHostOffers();
    } catch (e: any) {
      toast({ title: "Erro", description: tAuto("auto.pages_explorar.29", "Erro ao enviar contraproposta."), variant: "destructive" });
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  // Render the view
  if (isRoleResolving) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isHostOnly) {
    const getPropertyStatusBadge = (status: string) => {
      const map: Record<string, { label: string; cls: string }> = {
        active: { label: "Ativo", cls: "text-emerald-700 bg-emerald-50 border-emerald-200/80 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-800/30" },
        draft: { label: "Rascunho", cls: "text-muted-foreground bg-muted/60 border-border" },
        inactive: { label: "Inativo", cls: "text-muted-foreground bg-muted/40 border-border" },
      };
      const s = map[status] || { label: status, cls: "text-muted-foreground bg-muted border-border" };
      return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border tracking-wide uppercase ${s.cls}`}>{s.label}</span>;
    };

    const rawFirstName = user?.user_metadata?.full_name
      ? user.user_metadata.full_name.trim().split(/\s+/)[0]
      : (user?.user_metadata?.first_name || "");
    const ownerFirstName = rawFirstName ? rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1) : "";

    const totalActiveProperties = hostProperties?.filter(p => p.status === "active").length || 0;
    const totalConfirmedBookings = hostBookings?.filter(b => b.status === "confirmed").length || 0;
    
    const monthlyEarnings = hostBookings
      ? hostBookings
          .filter(b => {
            if (b.status !== "confirmed" && b.status !== "completed") return false;
            const bDate = new Date(b.created_at);
            const now = new Date();
            return bDate.getMonth() === now.getMonth() && bDate.getFullYear() === now.getFullYear();
          })
          .reduce((sum, b) => sum + (b.host_payout || 0), 0)
      : 0;

    const averageRating = hostReviews && hostReviews.length > 0
      ? (hostReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / hostReviews.length).toFixed(1)
      : (hostProperties && hostProperties.filter(p => p.average_rating).length > 0
          ? (hostProperties.reduce((sum, p) => sum + (p.average_rating || 0), 0) / hostProperties.filter(p => p.average_rating).length).toFixed(1)
          : "0.0");

    const filteredHostProperties = hostProperties
      ? hostProperties.filter(p => {
          if (!hostSearchQuery) return true;
          const query = hostSearchQuery.toLowerCase();
          return (
            p.title?.toLowerCase().includes(query) ||
            p.city?.toLowerCase().includes(query) ||
            p.province?.toLowerCase().includes(query) ||
            p.address?.toLowerCase().includes(query)
          );
        })
      : [];

    // ===== Real analytics based on tracking tables + bookings =====
    const parsePeriod = (p: string): { year: number; month: number } => {
      const m = /^(\d{4})-(\d{2})$/.exec(p);
      if (m) return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) - 1 };
      const d = new Date();
      return { year: d.getFullYear(), month: d.getMonth() };
    };
    const { year: selYear, month: selMonth } = parsePeriod(selectedPeriod);

    const countInMonth = <T,>(rows: T[], pickDate: (r: T) => string | Date | null | undefined, year: number, month: number) => {
      let c = 0;
      for (const r of rows) {
        const raw = pickDate(r);
        if (!raw) continue;
        const d = new Date(raw as any);
        if (d.getFullYear() === year && d.getMonth() === month) c++;
      }
      return c;
    };

    const viewsCurrent = countInMonth(hostViewsYear as any[], (r: any) => r.viewed_at, selYear, selMonth);
    const clicksCurrent = countInMonth(hostClicksYear as any[], (r: any) => r.clicked_at, selYear, selMonth);

    const prevMonth = selMonth === 0 ? 11 : selMonth - 1;
    const prevYear = selMonth === 0 ? selYear - 1 : selYear;
    const viewsPrev = countInMonth(hostViewsYear as any[], (r: any) => r.viewed_at, prevYear, prevMonth);
    const clicksPrev = countInMonth(hostClicksYear as any[], (r: any) => r.clicked_at, prevYear, prevMonth);

    // Confirmed bookings in selected month (created_at as proxy)
    const bookingsInMonth = (year: number, month: number) =>
      (hostBookings || []).filter((b: any) => {
        if (b.status !== "confirmed" && b.status !== "completed") return false;
        const d = new Date(b.created_at);
        return d.getFullYear() === year && d.getMonth() === month;
      }).length;
    const confirmedInSel = bookingsInMonth(selYear, selMonth);

    // Nights occupied in selected month
    const daysInMonth = new Date(selYear, selMonth + 1, 0).getDate();
    const monthStart = new Date(selYear, selMonth, 1);
    const monthEnd = new Date(selYear, selMonth + 1, 1);
    let nightsOccupied = 0;
    for (const b of (hostBookings || []) as any[]) {
      if (b.status !== "confirmed" && b.status !== "completed") continue;
      const ci = new Date(b.check_in);
      const co = new Date(b.check_out);
      const start = ci > monthStart ? ci : monthStart;
      const end = co < monthEnd ? co : monthEnd;
      const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (diff > 0) nightsOccupied += diff;
    }
    const occupancyDenominator = Math.max(1, totalActiveProperties * daysInMonth);
    const occupancyRate = totalActiveProperties > 0
      ? Math.min(100, (nightsOccupied / occupancyDenominator) * 100)
      : 0;

    const simulatedViews = viewsCurrent.toLocaleString("pt-AO");
    const simulatedClicks = clicksCurrent.toLocaleString("pt-AO");
    const simulatedConversion = viewsCurrent > 0
      ? ((confirmedInSel / viewsCurrent) * 100).toFixed(1)
      : "0.0";

    const pctChange = (curr: number, prev: number): number => {
      if (prev <= 0) return curr > 0 ? 100 : 0;
      return Math.round(((curr - prev) / prev) * 100);
    };
    const viewsTrend = pctChange(viewsCurrent, viewsPrev);
    const clicksTrend = pctChange(clicksCurrent, clicksPrev);
    const conversionPrev = viewsPrev > 0 ? (bookingsInMonth(prevYear, prevMonth) / viewsPrev) * 100 : 0;
    const conversionTrend = pctChange(
      viewsCurrent > 0 ? (confirmedInSel / viewsCurrent) * 100 : 0,
      conversionPrev
    );

    if (isMobile) {
      return (
        <div className="flex flex-col h-[100dvh] overflow-hidden bg-[#F8F9FA] dark:bg-[#121212]">
          <Header />
          <SEOHead
            title={tAuto("auto.pages_explorar.30", "Painel do Proprietário - BeraStays")}
            description={tAuto("auto.pages_explorar.31", "Gerencie seus imóveis, reservas e ganhos na plataforma BeraStays.")}
            keywords="proprietário, painel, berastays, imóveis"
            url="https://bera-stays.lovable.app/explorar"
          />
          
          <main className="flex-1 overflow-y-auto pt-[60px] pb-[calc(4.5rem+var(--safe-bottom))] px-4">
            
            {/* Title & Subtitle */}
            <div className="mt-4 mb-4">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-zinc-100 font-display">
                {ownerFirstName ? `Olá, ${ownerFirstName}!` : "Painel do Proprietário"}
              </h1>
              <p className="text-xs text-gray-500 dark:text-zinc-400">{tAuto("auto.pages_explorar.32", "Gestão dos seus espaços")}</p>
            </div>

            {/* 2x2 Stats Grid - Optimized vertical layout for mobile screens to avoid distortion */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Imóveis */}
              <div className="bg-[#FFFDFB] dark:bg-[#1C1816] border border-amber-100/30 dark:border-amber-950/20 rounded-2xl p-4 shadow-[0_8px_30px_rgba(232,81,61,0.015)] relative overflow-hidden flex flex-col justify-between min-h-[120px]">
                <div className="absolute bottom-0 right-0 w-16 h-16 bg-[#FCEEE3]/40 dark:bg-[#2C1D14]/10 rounded-tl-full -mr-2 -mb-2 z-0" />
                <div className="flex items-center justify-between z-10">
                  <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
                    <div className="absolute inset-0 bg-[#FFF0E6] dark:bg-[#2C1F16]/50 rounded-full scale-[1.05] opacity-80" />
                    <img src="/dashboard_house_icon.png" alt={tAuto("auto.pages_explorar.33", "Imóveis")} className="w-8 h-8 object-contain relative z-10" />
                  </div>
                  <span className="text-[9px] font-extrabold text-[#A09085] dark:text-zinc-500 uppercase tracking-wider">{tAuto("auto.pages_explorar.34", "Imóveis")}</span>
                </div>
                <div className="mt-2 text-left z-10">
                  <h3 className="text-2xl font-extrabold text-[#C2493A] dark:text-[#E86150] font-serif leading-tight">{totalActiveProperties}</h3>
                  <p className="text-[10px] font-semibold text-[#8C7E74] dark:text-zinc-400">Ativos</p>
                </div>
              </div>

              {/* Reservas */}
              <div className="bg-[#FFFDFB] dark:bg-[#161A18] border border-emerald-100/30 dark:border-emerald-950/20 rounded-2xl p-4 shadow-[0_8px_30px_rgba(16,185,129,0.015)] relative overflow-hidden flex flex-col justify-between min-h-[120px]">
                <div className="absolute bottom-0 right-0 w-16 h-16 bg-[#E6F0E8]/70 dark:bg-[#162319]/10 rounded-tl-full -mr-2 -mb-2 z-0" />
                <div className="flex items-center justify-between z-10">
                  <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
                    <div className="absolute inset-0 bg-[#EEF6F0] dark:bg-[#18281D]/50 rounded-full scale-[1.05] opacity-80" />
                    <img src="/dashboard_calendar_icon.png" alt="Reservas" className="w-8 h-8 object-contain relative z-10" />
                  </div>
                  <span className="text-[9px] font-extrabold text-[#90A093] dark:text-zinc-500 uppercase tracking-wider">{tAuto("host.explore.bookings", "Reservas")}</span>
                </div>
                <div className="mt-2 text-left z-10">
                  <h3 className="text-2xl font-extrabold text-[#3B6645] dark:text-[#5E9E6D] font-serif leading-tight">{totalConfirmedBookings}</h3>
                  <p className="text-[10px] font-semibold text-[#7A8A7D] dark:text-zinc-400">Confirmadas</p>
                </div>
              </div>

              {/* Ganhos */}
              <div className="bg-[#FFFDFB] dark:bg-[#16181C] border border-[#BACCDC]/30 dark:border-zinc-800/20 rounded-2xl p-4 shadow-[0_8px_30px_rgba(30,41,59,0.015)] relative overflow-hidden flex flex-col justify-between min-h-[120px]">
                <div className="absolute bottom-0 right-0 w-16 h-16 bg-[#E8ECF2]/70 dark:bg-[#1C202C]/10 rounded-tl-full -mr-2 -mb-2 z-0" />
                <div className="flex items-center justify-between z-10">
                  <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
                    <div className="absolute inset-0 bg-[#F0F2F6] dark:bg-[#1C202C]/40 rounded-full scale-[1.05] opacity-80" />
                    <img src="/dashboard_wallet_icon.png" alt="Ganhos" className="w-8 h-8 object-contain relative z-10" />
                  </div>
                  <span className="text-[9px] font-extrabold text-[#9093A0] dark:text-zinc-500 uppercase tracking-wider">Ganhos</span>
                </div>
                <div className="mt-2 text-left z-10">
                  <h3 className="text-xl font-extrabold text-[#1B305B] dark:text-[#8AA7E8] font-serif leading-tight truncate" title={`${monthlyEarnings.toLocaleString("pt-AO")} Kz`}>
                    {monthlyEarnings.toLocaleString("pt-AO")} Kz
                  </h3>
                  <p className="text-[10px] font-semibold text-[#7A7D8A] dark:text-zinc-400">{tAuto("auto.pages_explorar.35", "Este mês")}</p>
                </div>
              </div>

              {/* Avaliação */}
              <div className="bg-[#FFFDFB] dark:bg-[#1C1A16] border border-amber-100/30 dark:border-amber-950/20 rounded-2xl p-4 shadow-[0_8px_30px_rgba(217,148,20,0.015)] relative overflow-hidden flex flex-col justify-between min-h-[120px]">
                <div className="absolute bottom-0 right-0 w-16 h-16 bg-[#FAF0D6]/70 dark:bg-[#2A2314]/10 rounded-tl-full -mr-2 -mb-2 z-0" />
                <div className="flex items-center justify-between z-10">
                  <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
                    <div className="absolute inset-0 bg-[#FFF9EA] dark:bg-[#2C2417]/50 rounded-full scale-[1.05] opacity-80" />
                    <img src="/dashboard_star_icon.png" alt={tAuto("auto.pages_explorar.36", "Avaliação")} className="w-8 h-8 object-contain relative z-10" />
                  </div>
                  <span className="text-[9px] font-extrabold text-[#A09885] dark:text-zinc-500 uppercase tracking-wider">{tAuto("auto.pages_explorar.37", "Avaliação")}</span>
                </div>
                <div className="mt-2 text-left z-10">
                  <h3 className="text-2xl font-extrabold text-[#D99414] dark:text-[#F3CD5F] font-serif leading-tight">{averageRating}</h3>
                  <p className="text-[10px] font-semibold text-[#8A8374] dark:text-zinc-400">{tAuto("auto.pages_explorar.38", "Média hóspedes")}</p>
                </div>
              </div>
            </div>

            {/* Insights Section */}
            <div className="bg-white dark:bg-[#1C1C1E] border border-gray-100 dark:border-[#2A2A2A] rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.015)] mb-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.39", "Insights do mês")}</h4>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="flex h-7 w-fit items-center gap-1 px-3 py-1 rounded-full border border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900 text-xs font-bold text-gray-700 dark:text-zinc-300 focus:ring-0 focus:ring-offset-0 focus:border-gray-200 hover:border-gray-300 dark:hover:border-zinc-700 transition-colors [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-gray-400 [&>svg]:ml-0.5 cursor-pointer shadow-none">
                    <SelectValue placeholder={tAuto("auto.pages_explorar.40", "Este mês")} />
                  </SelectTrigger>
                  <SelectContent className="min-w-[8rem] rounded-xl border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-1 shadow-md">
                    {periodOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs font-medium cursor-pointer rounded-lg">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2 divide-x divide-gray-100 dark:divide-zinc-800">
                <div className="flex flex-col items-center justify-center px-1">
                  <span className="text-gray-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider text-center">{tAuto("auto.pages_explorar.41", "Visualizações")}</span>
                  <span className="text-lg font-extrabold text-gray-800 dark:text-zinc-100 mt-1">{simulatedViews}</span>
                  <span className="text-emerald-500 text-[10px] font-bold mt-0.5 flex items-center gap-0.5">
                    <TrendingUp className="w-3.5 h-3.5" />
                    +{viewsTrend}%
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center px-1">
                  <span className="text-gray-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider text-center">Cliques</span>
                  <span className="text-lg font-extrabold text-gray-800 dark:text-zinc-100 mt-1">{simulatedClicks}</span>
                  <span className="text-emerald-500 text-[10px] font-bold mt-0.5 flex items-center gap-0.5">
                    <TrendingUp className="w-3.5 h-3.5" />
                    +{clicksTrend}%
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center px-1">
                  <span className="text-gray-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider text-center">{tAuto("auto.pages_explorar.42", "Conversão")}</span>
                  <span className="text-lg font-extrabold text-gray-800 dark:text-zinc-100 mt-1">{simulatedConversion}%</span>
                  <span className="text-emerald-500 text-[10px] font-bold mt-0.5 flex items-center gap-0.5">
                    <TrendingUp className="w-3.5 h-3.5" />
                    +{conversionTrend}%
                  </span>
                </div>
              </div>
            </div>
          </main>

          <PropertyFilters
            filters={filters}
            onFiltersChange={setFilters}
            totalResults={filteredHostProperties.length}
            variant="host"
            isOpen={isFilterOpen}
            onOpenChange={setIsFilterOpen}
          />

          <EditPropertyDialog
            property={editProperty}
            open={!!editProperty}
            onOpenChange={(open) => { if (!open) setEditProperty(null); }}
            onSaved={() => refetchHostProperties()}
          />

          <ShareSpaceDialog
            open={!!shareProperty}
            onOpenChange={(o) => { if (!o) setShareProperty(null); }}
            propertyId={shareProperty?.id || ""}
            propertyTitle={shareProperty?.title}
          />
        </div>
      );
    }

    // Selected dates inside the inline calendar highlights
    const selectedDates = hostBookings
      ? hostBookings
          .filter((b: any) => b.status === "confirmed" || b.status === "completed")
          .flatMap((b: any) => {
            const checkIn = new Date(b.check_in);
            const checkOut = new Date(b.check_out);
            const dates = [];
            let current = new Date(checkIn);
            while (current <= checkOut) {
              dates.push(new Date(current));
              current.setDate(current.getDate() + 1);
            }
            return dates;
          })
      : [];

    const confirmedBookings = hostBookings
      ? [...hostBookings]
          .filter((b: any) => b.status === "confirmed")
          .sort((a: any, b: any) => new Date(a.check_in).getTime() - new Date(b.check_in).getTime())
      : [];

    // Insurance requests variables moved to top-level to satisfy Rules of Hooks

    const todayStr = format(new Date(), "yyyy-MM-dd");
    const todayCheckIns = hostBookings?.filter(b => b.status === "confirmed" && b.check_in === todayStr).length || 0;
    const todayCheckOuts = hostBookings?.filter(b => b.status === "confirmed" && b.check_out === todayStr).length || 0;
    const pendingBookingsCount = hostBookings?.filter(b => b.status === "pending" || b.status === "awaiting_confirmation").length || 0;

    // Real monthly chart: Jan..currentMonth of current year
    const monthLabels = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const currentYear = new Date().getFullYear();
    const currentMonthIdx = new Date().getMonth();
    const monthlyData = monthLabels.slice(0, currentMonthIdx + 1).map((name, idx) => {
      const v = countInMonth(hostViewsYear as any[], (r: any) => r.viewed_at, currentYear, idx);
      const c = countInMonth(hostClicksYear as any[], (r: any) => r.clicked_at, currentYear, idx);
      return { name, visualizacoes: v, cliques: c, conversao: v > 0 ? Number(((c / v) * 100).toFixed(1)) : 0 };
    });

    const renderDashboard = () => {
      return (
        <div className="space-y-6">
          {/* 4 Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Imóveis */}
            <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-[24px] p-5 shadow-sm relative overflow-hidden flex items-center justify-between min-h-[120px]">
              <div className="absolute bottom-0 right-0 w-20 h-20 bg-[#FFF0E6]/50 dark:bg-[#2C1F16]/5 rounded-tl-full -mr-1 -mb-1" />
              <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
                <div className="absolute inset-0 bg-[#FFF0E6] dark:bg-[#2C1F16]/40 rounded-full" />
                <img src="/dashboard_house_icon.png" alt={tAuto("auto.pages_explorar.43", "Imóveis")} className="w-[50px] h-[50px] object-contain relative z-10" />
              </div>
              <div className="text-right flex-1 min-w-0 pl-2">
                <p className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.15em]">{tAuto("auto.pages_explorar.44", "Imóveis")}</p>
                <h3 className="text-3xl font-black text-primary leading-none mt-1.5">{totalActiveProperties}</h3>
                <p className="text-[11px] font-semibold text-zinc-500 mt-1">Ativos</p>
              </div>
            </div>

            {/* Reservas */}
            <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-[24px] p-5 shadow-sm relative overflow-hidden flex items-center justify-between min-h-[120px]">
              <div className="absolute bottom-0 right-0 w-20 h-20 bg-[#EEF6F0]/50 dark:bg-[#18281D]/5 rounded-tl-full -mr-1 -mb-1" />
              <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
                <div className="absolute inset-0 bg-[#EEF6F0] dark:bg-[#18281D]/40 rounded-full" />
                <img src="/dashboard_calendar_icon.png" alt="Reservas" className="w-[50px] h-[50px] object-contain relative z-10" />
              </div>
              <div className="text-right flex-1 min-w-0 pl-2">
                <p className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.15em]">{tAuto("host.explore.bookings", "Reservas")}</p>
                <h3 className="text-3xl font-black text-emerald-600 dark:text-emerald-500 leading-none mt-1.5">{totalConfirmedBookings}</h3>
                <p className="text-[11px] font-semibold text-zinc-500 mt-1">Confirmadas</p>
              </div>
            </div>

            {/* Ganhos */}
            <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-[24px] p-5 shadow-sm relative overflow-hidden flex items-center justify-between min-h-[120px]">
              <div className="absolute bottom-0 right-0 w-20 h-20 bg-[#F0F2F6]/50 dark:bg-[#1C202C]/5 rounded-tl-full -mr-1 -mb-1" />
              <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
                <div className="absolute inset-0 bg-[#F0F2F6] dark:bg-[#1C202C]/40 rounded-full" />
                <img src="/dashboard_wallet_icon.png" alt="Ganhos" className="w-[50px] h-[50px] object-contain relative z-10" />
              </div>
              <div className="text-right flex-1 min-w-0 pl-2">
                <p className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.15em]">Ganhos</p>
                <h3 className="text-xl font-black text-[#1B305B] dark:text-[#8AA7E8] leading-none mt-2 truncate" title={`${monthlyEarnings.toLocaleString("pt-AO")} Kz`}>
                  {monthlyEarnings.toLocaleString("pt-AO")} Kz
                </h3>
                <p className="text-[11px] font-semibold text-zinc-500 mt-1">{tAuto("auto.pages_explorar.45", "Este mês")}</p>
              </div>
            </div>

            {/* Avaliação */}
            <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-[24px] p-5 shadow-sm relative overflow-hidden flex items-center justify-between min-h-[120px]">
              <div className="absolute bottom-0 right-0 w-20 h-20 bg-[#FFF9EA]/50 dark:bg-[#2C2417]/5 rounded-tl-full -mr-1 -mb-1" />
              <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
                <div className="absolute inset-0 bg-[#FFF9EA] dark:bg-[#2C2417]/40 rounded-full" />
                <img src="/dashboard_star_icon.png" alt={tAuto("auto.pages_explorar.46", "Avaliação")} className="w-[50px] h-[50px] object-contain relative z-10" />
              </div>
              <div className="text-right flex-1 min-w-0 pl-2">
                <p className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.15em]">{tAuto("auto.pages_explorar.47", "Avaliação")}</p>
                <h3 className="text-3xl font-black text-amber-550 leading-none mt-1.5">{averageRating}</h3>
                <p className="text-[11px] font-semibold text-zinc-500 mt-1">{tAuto("auto.pages_explorar.48", "Média hóspedes")}</p>
              </div>
            </div>
          </div>

          {/* Monthly Summary Section */}
          <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.49", "Resumo do Mês")}</h4>
                <p className="text-[11px] text-zinc-400">{tAuto("auto.pages_explorar.50", "Métricas de visualização e conversão de seus anúncios")}</p>
              </div>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="flex h-8 w-fit items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-900 text-xs font-bold text-gray-700 dark:text-zinc-300">
                  <SelectValue placeholder={tAuto("auto.pages_explorar.51", "Este mês")} />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs font-medium">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              {/* Metrics lists */}
              <div className="lg:col-span-5 grid grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-gray-50/50 dark:bg-zinc-800/10 border border-gray-100 dark:border-zinc-800/30 flex flex-col justify-center">
                  <span className="text-zinc-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider">{tAuto("auto.pages_explorar.52", "Visualizações")}</span>
                  <span className="text-xl font-extrabold text-gray-800 dark:text-zinc-100 mt-1">{simulatedViews}</span>
                  <span className="text-emerald-500 text-[10px] font-bold mt-1 flex items-center gap-0.5">
                    <TrendingUp className="w-3.5 h-3.5" />
                    +{viewsTrend}%
                  </span>
                </div>
                <div className="p-4 rounded-2xl bg-gray-50/50 dark:bg-zinc-800/10 border border-gray-100 dark:border-zinc-800/30 flex flex-col justify-center">
                  <span className="text-zinc-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider">Cliques</span>
                  <span className="text-xl font-extrabold text-gray-800 dark:text-zinc-100 mt-1">{simulatedClicks}</span>
                  <span className="text-emerald-500 text-[10px] font-bold mt-1 flex items-center gap-0.5">
                    <TrendingUp className="w-3.5 h-3.5" />
                    +{clicksTrend}%
                  </span>
                </div>
                <div className="p-4 rounded-2xl bg-gray-50/50 dark:bg-zinc-800/10 border border-gray-100 dark:border-zinc-800/30 flex flex-col justify-center">
                  <span className="text-zinc-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider">{tAuto("auto.pages_explorar.53", "Conversão")}</span>
                  <span className="text-xl font-extrabold text-gray-800 dark:text-zinc-100 mt-1">{simulatedConversion}%</span>
                  <span className="text-emerald-500 text-[10px] font-bold mt-1 flex items-center gap-0.5">
                    <TrendingUp className="w-3.5 h-3.5" />
                    +{conversionTrend}%
                  </span>
                </div>
                <div className="p-4 rounded-2xl bg-gray-50/50 dark:bg-zinc-800/10 border border-gray-100 dark:border-zinc-800/30 flex flex-col justify-center">
                  <span className="text-zinc-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-wider">{tAuto("auto.pages_explorar.54", "Taxa Ocupação")}</span>
                  <span className="text-xl font-extrabold text-gray-800 dark:text-zinc-100 mt-1">
                    {occupancyRate.toFixed(1)}%
                  </span>
                  <span className="text-zinc-400 dark:text-zinc-500 text-[10px] mt-1 font-semibold">{tAuto("auto.pages_explorar.55", "Este mês")}</span>
                </div>
              </div>

              {/* Area chart */}
              <div className="lg:col-span-7 bg-gray-50/30 dark:bg-zinc-800/5 rounded-2xl p-2">
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E8513D" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#E8513D" stopOpacity={0.0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <ChartTooltip />
                    <Area type="monotone" dataKey="visualizacoes" name="Visualizações" stroke="#E8513D" strokeWidth={2.5} fillOpacity={1} fill="url(#colorViews)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Calendar & Upcoming Bookings Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Occupation Calendar */}
            <div className="lg:col-span-5 bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-4">
              <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.56", "Calendário de Ocupação")}</h4>
              <div className="border border-gray-100 dark:border-[#2A2A2A] rounded-2xl p-2.5 bg-gray-50/40 dark:bg-zinc-800/10 flex justify-center">
                <Calendar
                  mode="multiple"
                  selected={selectedDates}
                  className="rounded-xl shadow-none bg-background mx-auto"
                />
              </div>
              <div className="flex items-center gap-4 text-[10px] text-zinc-400 font-bold justify-center">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded bg-primary" /> Ocupado / Reservado
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded border border-gray-200 bg-background" /> Disponível
                </span>
              </div>
            </div>

            {/* Upcoming Reservations */}
            <div className="lg:col-span-7 bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-4">
              <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.57", "Próximas Reservas")}</h4>
              {confirmedBookings.length > 0 ? (
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {confirmedBookings.slice(0, 5).map((b: any) => {
                    const images = getSafeImageUrls(b.properties?.images);
                    const heroImage = images.length > 0 ? images[0] : getFirstImageUrl(null);
                    return (
                      <div key={b.id} className="flex items-center justify-between p-3 rounded-2xl border border-gray-100 dark:border-zinc-800/40 hover:border-gray-200 transition-all bg-gray-50/20">
                        <div className="flex items-center gap-3 min-w-0">
                          <img src={heroImage} alt="" className="w-10 h-10 rounded-xl object-cover shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-gray-800 dark:text-zinc-200 truncate">{b.properties?.title || "Imóvel"}</p>
                            <p className="text-[10px] text-zinc-400 font-semibold mt-0.5">
                              {format(new Date(b.check_in), "dd MMM", { locale: getDateLocale() })} — {format(new Date(b.check_out), "dd MMM yyyy", { locale: getDateLocale() })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 flex flex-col items-end gap-1">
                          <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 dark:text-emerald-400 px-2 py-0.5 rounded-full uppercase">
                            Confirmada
                          </span>
                          <span className="text-[10px] text-zinc-400 font-bold">{b.guests?.full_name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[280px] flex flex-col items-center justify-center border border-dashed border-gray-200 dark:border-zinc-800/60 rounded-2xl p-6 text-center bg-gray-50/10">
                  <div className="w-16 h-16 rounded-full bg-orange-50/80 dark:bg-zinc-800/30 flex items-center justify-center mb-3">
                    <CalendarIcon className="w-7 h-7 text-primary" />
                  </div>
                  <h5 className="text-xs font-bold text-zinc-800 dark:text-zinc-200">{tAuto("auto.pages_explorar.58", "Nenhuma reserva confirmada")}</h5>
                  <p className="text-[11px] text-zinc-400 max-w-xs mt-1 leading-relaxed">
                    Você ainda não tem reservas confirmadas para as próximas datas. Suas reservas pendentes aparecerão na aba Reservas.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Panel */}
          <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.59", "Ações Rápidas")}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: "Visualizar ofertas", icon: FileText, action: () => setActiveMenu("pedidos_oferta") },
                { label: "Histórico financeiro", icon: Wallet, action: () => setActiveMenu("financeiro") },
                { label: "Desempenho & Análise", icon: TrendingUp, action: () => setActiveMenu("desempenho") },
              ].map((btn, idx) => (
                <button
                  key={idx}
                  onClick={btn.action}
                  className="p-4 rounded-2xl border border-gray-100 dark:border-zinc-800/40 hover:border-primary/20 dark:hover:border-primary/20 hover:bg-primary/[0.02] flex flex-col items-center justify-center text-center gap-2.5 active:scale-95 transition-all bg-gray-50/10"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-50/50 dark:bg-zinc-800/50 text-primary flex items-center justify-center shrink-0">
                    <btn.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[11px] font-bold text-zinc-650 dark:text-zinc-300 leading-tight">
                    {btn.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Spaces Table */}
          <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.60", "Os seus espaços")}</h4>
                <p className="text-[11px] text-zinc-455 dark:text-zinc-500 font-medium">{tAuto("auto.pages_explorar.61", "Gerenciamento de todos os seus anúncios de imóveis na BeraStays")}</p>
              </div>
            </div>

            {filteredHostProperties.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-zinc-800 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                      <th className="pb-3 pl-2">{tAuto("auto.pages_explorar.62", "Imóvel")}</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3">{tAuto("auto.pages_explorar.63", "Preço / Noite")}</th>
                      <th className="pb-3">{tAuto("auto.pages_explorar.64", "Visualizações")}</th>
                      <th className="pb-3">{tAuto("host.explore.bookings", "Reservas")}</th>
                      <th className="pb-3 text-right pr-2">{tAuto("auto.pages_explorar.65", "Ações")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-zinc-850">
                    {filteredHostProperties.map((p: any) => {
                      const images = getSafeImageUrls(p.images);
                      const heroImage = images.length > 0 ? images[0] : getFirstImageUrl(null);
                      const bookingsCount = hostBookings?.filter(b => b.property_id === p.id).length || 0;
                      return (
                        <tr key={p.id} className="text-xs group hover:bg-gray-50/20 dark:hover:bg-zinc-800/10 transition-colors">
                          <td className="py-3.5 pl-2 flex items-center gap-3 min-w-[200px]">
                            <img src={heroImage} alt="" className="w-11 h-11 rounded-xl object-cover shrink-0" />
                            <div className="min-w-0">
                              <p className="font-bold text-gray-800 dark:text-zinc-200 truncate leading-snug">{p.title}</p>
                              <span className="text-[10px] text-zinc-400 truncate block mt-0.5">{p.city}, {p.province}</span>
                            </div>
                          </td>
                          <td className="py-3.5">
                            {getPropertyStatusBadge(p.status || "draft")}
                          </td>
                          <td className="py-3.5 font-bold text-zinc-700 dark:text-zinc-300">
                            {p.price_per_night.toLocaleString("pt-AO")} Kz
                          </td>
                          <td className="py-3.5 font-medium text-zinc-500">
                            {Math.round(1284 * (p.average_rating || 4.2) / 4.5).toLocaleString("pt-AO")}
                          </td>
                          <td className="py-3.5 font-bold text-primary">
                            {bookingsCount}
                          </td>
                          <td className="py-3.5 text-right pr-2">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={() => setEditProperty(p)}
                                className="h-7 text-[10px] font-bold rounded-lg border-gray-200 flex items-center gap-1 hover:border-primary/30"
                              >
                                <Pencil className="w-3 h-3 text-zinc-500" />
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={() => navigate(`/imovel/${p.id}?preview=host`, { state: { hostPreview: true } })}
                                className="h-7 text-[10px] font-bold rounded-lg border-gray-200 flex items-center gap-1 hover:border-primary"
                              >
                                <Eye className="w-3.5 h-3.5 text-zinc-500" />
                                Ver
                              </Button>
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={() => setShareProperty({ id: p.id, title: p.title })}
                                className="h-7 text-[10px] font-bold rounded-lg border-gray-200 flex items-center gap-1 hover:border-primary"
                              >
                                <Share2 className="w-3 h-3 text-zinc-500" />
                                Link
                              </Button>
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={() => setDeletePropertyId(p.id)}
                                className="h-7 text-[10px] font-bold rounded-lg border-red-200/50 hover:border-red-400 text-red-500 hover:bg-red-50/50"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-gray-200 dark:border-zinc-800 rounded-3xl bg-gray-50/5">
                <Home className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
                <h5 className="font-bold text-xs">{tAuto("auto.pages_explorar.66", "Nenhum espaço cadastrado")}</h5>
                <p className="text-[11px] text-zinc-400 max-w-xs mx-auto mt-1 leading-relaxed">
                  Adicione o seu primeiro anúncio de imóvel para começar a receber reservas na BeraStays.
                </p>
                <Button
                  size="sm"
                  onClick={() => navigate("/anunciar")}
                  className="mt-4 bg-primary hover:bg-[#D14430] text-white text-xs font-bold rounded-xl"
                >
                  Cadastrar meu espaço
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderImoveis = () => {
      return (
        <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-zinc-800/40 pb-5">
            <div>
              <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.67", "Meus Imóveis")}</h4>
              <p className="text-[11px] text-zinc-400">Total de {hostProperties?.length || 0} imóveis cadastrados</p>
            </div>
            
            <div className="flex items-center gap-3">
              <Input
                placeholder={tAuto("auto.pages_explorar.68", "Pesquisar meus imóveis...")}
                value={hostSearchQuery}
                onChange={(e) => setHostSearchQuery(e.target.value)}
                className="h-9 w-60 rounded-xl text-xs"
              />
              <Button
                onClick={() => navigate("/anunciar")}
                className="h-9 bg-primary hover:bg-[#D14430] text-white text-xs font-bold rounded-xl gap-2 shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Adicionar imóvel
              </Button>
            </div>
          </div>

          {!isVerified && (
            <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/30">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs font-bold text-amber-800 dark:text-amber-300">{tAuto("auto.pages_explorar.69", "Perfil não verificado")}</h4>
                <p className="text-[11px] text-amber-700/90 dark:text-amber-400/90 mt-0.5 leading-relaxed">
                  Verifique a sua identidade nas definições para poder adicionar novos imóveis e receber reservas.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/perfil")}
                  className="h-7 text-[10px] mt-2.5 border-amber-300 text-amber-800 hover:bg-amber-100/50"
                >
                  Verificar Agora
                </Button>

              </div>
            </div>
          )}

          {filteredHostProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredHostProperties.map((p: any) => {
                const images = getSafeImageUrls(p.images);
                const heroImage = images.length > 0 ? images[0] : getFirstImageUrl(null);
                return (
                  <div key={p.id} className="border border-gray-150 dark:border-zinc-800/40 rounded-2xl p-4 flex gap-4 hover:border-primary/20 transition-all bg-gray-50/10">
                    <img src={heroImage} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0 bg-muted" />
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h5 className="font-bold text-sm text-gray-800 dark:text-zinc-200 truncate">{p.title}</h5>
                          {getPropertyStatusBadge(p.status || "draft")}
                        </div>
                        <p className="text-[11px] text-zinc-400 truncate mt-1 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                          {p.city}, {p.province}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-zinc-800/30">
                        <span className="text-xs font-bold text-primary">{p.price_per_night.toLocaleString("pt-AO")} Kz/noite</span>
                        <div className="flex items-center gap-1.5">
                          <Button size="xs" variant="outline" onClick={() => setEditProperty(p)} className="h-7 px-2.5 text-[10px] font-bold rounded-lg border-gray-200">
                            Editar
                          </Button>
                          <Button size="xs" variant="outline" onClick={() => navigate(`/imovel/${p.id}?preview=host`, { state: { hostPreview: true } })} className="h-7 px-2.5 text-[10px] font-bold rounded-lg border-gray-200">
                            Ver
                          </Button>
                          <Button size="xs" variant="outline" onClick={() => setShareProperty({ id: p.id, title: p.title })} className="h-7 px-2.5 text-[10px] font-bold rounded-lg border-gray-200 gap-1">
                            <Share2 className="w-3 h-3" />
                            Link
                          </Button>
                          <Button size="xs" variant="outline" onClick={() => setDeletePropertyId(p.id)} className="h-7 px-2 text-[10px] font-bold rounded-lg border-red-100 hover:border-red-300 text-red-500">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-400">
              Nenhum imóvel encontrado correspondente à pesquisa.
            </div>
          )}
        </div>
      );
    };

    const renderReservas = () => {
      const activeBookings = hostBookings || [];
      const filtered = bookingFilter === "all" ? activeBookings : activeBookings.filter((b: any) => b.status === bookingFilter);

      const awaitingConfirmationCount = activeBookings.filter((b: any) => b.status === "awaiting_confirmation").length;

      return (
        <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-6">
          {/* Header & Filter Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 dark:border-zinc-800/40 pb-5">
            <div>
              <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.70", "Gerenciamento de Reservas")}</h4>
              <p className="text-[11px] text-zinc-400">Total de {filtered.length} reservas registradas</p>
            </div>
            
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
              {[
                { id: "all", label: "Todas" },
                { id: "awaiting_confirmation", label: "Aguardam Aprovação" },
                { id: "awaiting_payment", label: "Aguardam Pagamento" },
                { id: "confirmed", label: "Confirmadas" },
                { id: "completed", label: "Concluídas" },
                { id: "cancelled", label: "Canceladas" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setBookingFilter(tab.id)}
                  className={cn(
                    "px-3.5 py-1.5 text-xs font-bold rounded-full transition-all duration-200",
                    bookingFilter === tab.id
                      ? "bg-primary text-white shadow-sm"
                      : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-gray-55/40 dark:hover:bg-zinc-800/50"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Awaiting Confirmation Alert Banner */}
          {awaitingConfirmationCount > 0 && bookingFilter === "all" && (
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-warning/10 dark:from-amber-950/20 dark:to-orange-950/10 border border-amber-200/40 dark:border-amber-900/30 shadow-sm animate-pulse">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
                Tem {awaitingConfirmationCount} reserva{awaitingConfirmationCount > 1 ? "s" : ""} aguardando a sua aprovação.
              </p>
            </div>
          )}

          {filtered.length > 0 ? (
            <div className="space-y-4">
              {filtered.map((b: any) => {
                const images = getSafeImageUrls(b.properties?.images);
                const heroImage = images.length > 0 ? images[0] : getFirstImageUrl(null);
                
                return (
                  <div key={b.id} className="border border-gray-100 dark:border-zinc-850/65 rounded-[28px] p-4 bg-white dark:bg-[#1C1C20]/40 shadow-[0_2px_12px_rgba(0,0,0,0.01)] hover:border-gray-200 dark:hover:border-zinc-800 transition-all flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-4">
                      {/* Left side: circular thumbnail and details */}
                      <div className="flex items-center gap-4 min-w-0">
                        <img src={heroImage} alt="" className="w-14 h-14 rounded-full object-cover shrink-0 border border-gray-100/50 dark:border-zinc-850/50" />
                        <div className="min-w-0 space-y-1">
                          <h5 className="font-bold text-sm text-gray-850 dark:text-zinc-200 truncate">{b.properties?.title || "Imóvel"}</h5>
                          <p className="text-[11px] text-zinc-400 font-semibold">
                            {format(new Date(b.check_in), "dd 'de' MMMM", { locale: getDateLocale() })} — {format(new Date(b.check_out), "dd 'de' MMMM yyyy", { locale: getDateLocale() })}
                          </p>
                          <p className="text-[10px] text-zinc-400 font-bold">
                            Hóspede: <span className="text-zinc-650 dark:text-zinc-350">{b.guests?.full_name || "Hóspede"}</span> • <span className="text-zinc-500">{b.guests?.phone || "Sem contacto"}</span>
                          </p>
                        </div>
                      </div>

                      {/* Right side: price and status badge */}
                      <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                        <p className="text-sm font-extrabold text-gray-850 dark:text-zinc-100">{b.total_amount.toLocaleString("pt-AO")} Kz</p>
                        {(() => {
                          // Apenas marcar como "Concluída" se houver check-in/check-out efetivo + pagamento.
                          const isTrulyCompleted =
                            b.status === "completed" &&
                            b.payment_status === "paid" &&
                            !!b.checked_in_at;
                          const displayStatus = (() => {
                            if (b.status === "cancelled") return "cancelled";
                            if (b.status === "awaiting_payment" || (b.payment_status === "pending" && b.status === "confirmed" && false)) return "awaiting_payment";
                            if (b.status === "awaiting_confirmation") return "awaiting_confirmation";
                            if (b.status === "pending") return "pending";
                            if (b.status === "confirmed") return "confirmed";
                            if (isTrulyCompleted) return "completed";
                            // Fallback seguro — nunca classificar como "Concluída" sem prova de check-in/pagamento
                            return b.status === "completed" ? "confirmed" : b.status;
                          })();
                          const map: Record<string, { label: string; cls: string }> = {
                            confirmed: { label: "Confirmada", cls: "bg-emerald-50 text-emerald-600 border-emerald-100/30 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/20" },
                            pending: { label: "Pendente", cls: "bg-amber-50 text-amber-600 border-amber-100/30 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/20" },
                            awaiting_confirmation: { label: "Aguarda Aprovação", cls: "bg-blue-50 text-blue-600 border-blue-100/30 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/20" },
                            awaiting_payment: { label: "Aguardando Pagamento", cls: "bg-primary/10 text-primary border-primary/30 dark:bg-primary/20 dark:text-primary dark:border-primary/20" },
                            cancelled: { label: "Cancelada", cls: "bg-red-50 text-red-605 border-red-100/30 dark:bg-red-950/20 dark:text-red-450 dark:border-red-900/20" },
                            completed: { label: "Concluída", cls: "bg-primary/5 text-primary border-primary/20" },
                          };
                          const s = map[displayStatus] || { label: displayStatus, cls: "bg-gray-50 text-gray-500 border-gray-200/50 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700/40" };
                          return (
                            <span
                              className={cn(
                                "px-2.5 py-0.5 text-[9px] font-extrabold rounded-full uppercase tracking-wider border",
                                s.cls
                              )}
                              title={
                                displayStatus === "awaiting_payment"
                                  ? "O hóspede ainda não efetuou o pagamento."
                                  : displayStatus === "awaiting_confirmation"
                                  ? "Esta reserva aguarda a sua aprovação."
                                  : displayStatus === "completed"
                                  ? "Estadia concluída — check-in e check-out registados."
                                  : undefined
                              }
                            >
                              {s.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Actions Row at bottom of card */}
                    {["pending", "awaiting_confirmation", "confirmed", "cancelled"].includes(b.status) && (
                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-50 dark:border-zinc-850/40">
                        {b.status === "awaiting_confirmation" && (
                          <>
                            <Button
                              size="xs"
                              disabled={actionLoading === b.id}
                              onClick={() => handleBookingAction(b.id, "approve")}
                              className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold rounded-xl px-4 shadow-sm"
                            >
                              {actionLoading === b.id ? "A aprovar..." : "Aprovar"}
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              disabled={actionLoading === b.id}
                              onClick={() => handleBookingAction(b.id, "reject")}
                              className="h-8 text-[11px] font-bold rounded-xl px-4 border-gray-200 hover:bg-red-50 hover:text-red-500 dark:border-zinc-800 dark:hover:bg-zinc-800/50"
                            >
                              Recusar
                            </Button>
                          </>
                        )}

                        {b.status === "pending" && (
                          <>
                            <Button
                              size="xs"
                              disabled={actionLoading === b.id}
                              onClick={() => handleBookingAction(b.id, "confirm")}
                              className="h-8 bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold rounded-xl px-4 shadow-sm"
                            >
                              {actionLoading === b.id ? "A confirmar..." : "Confirmar"}
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              disabled={actionLoading === b.id}
                              onClick={() => handleBookingAction(b.id, "cancel")}
                              className="h-8 text-[11px] font-bold rounded-xl px-4 border-gray-200 hover:bg-red-50 hover:text-red-500 dark:border-zinc-800 dark:hover:bg-zinc-800/50"
                            >
                              Cancelar
                            </Button>
                          </>
                        )}

                        {b.status === "confirmed" && (
                          <Button
                            size="xs"
                            variant="outline"
                            disabled={actionLoading === b.id}
                            onClick={() => handleBookingAction(b.id, "cancel")}
                            className="h-8 text-[11px] font-bold rounded-xl px-4 border-red-200 text-red-500 hover:bg-red-50 dark:border-red-950/20 dark:hover:bg-red-950/30"
                          >
                            Cancelar Reserva
                          </Button>
                        )}

                        {b.status === "cancelled" && (
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => setDeleteBookingId(b.id)}
                            className="h-8 text-[11px] font-bold rounded-xl px-4 border-gray-200 hover:bg-red-50 hover:text-red-500 dark:border-zinc-800 dark:hover:bg-zinc-800/50"
                          >
                            Eliminar Registo
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white dark:bg-[#1C1C20]/40 border border-gray-100 dark:border-[#2A2A2A] rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.005)]">
              <CalendarCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h5 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.71", "Nenhuma reserva encontrada")}</h5>
              <p className="text-xs text-zinc-550 dark:text-zinc-400 mt-1">{tAuto("auto.pages_explorar.72", "Quando os hóspedes reservarem os seus imóveis, eles aparecerão aqui.")}</p>
            </div>
          )}
        </div>
      );
    };

    const renderCalendario = () => {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.73", "Calendário de Ocupação")}</h4>
            <div className="border border-gray-100 dark:border-[#2A2A2A] rounded-2xl p-3 bg-gray-55/20 dark:bg-zinc-800/10 flex justify-center">
              <Calendar
                mode="multiple"
                selected={selectedDates}
                className="rounded-xl shadow-none bg-background mx-auto"
              />
            </div>
            <div className="flex items-start gap-2 p-3 bg-blue-50/30 dark:bg-blue-950/20 border border-blue-100/30 rounded-xl text-[10px] text-blue-700/80 leading-relaxed dark:text-blue-400">
              <HelpCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
              <span>
                As datas coloridas correspondem a reservas pagas ou estadias ativas. As reservas com status pendente não bloqueiam a data automaticamente até à confirmação por parte do proprietário.
              </span>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.74", "Detalhamento de Reservas Confirmadas")}</h4>
            {confirmedBookings.length > 0 ? (
              <div className="space-y-3">
                {confirmedBookings.map((b: any) => (
                  <div key={b.id} className="border border-gray-150 rounded-2xl p-4 bg-gray-50/10 hover:border-primary/20 transition-all flex justify-between items-center">
                    <div>
                      <p className="font-bold text-sm text-gray-850 dark:text-zinc-200">{b.properties?.title || "Imóvel"}</p>
                      <p className="text-[11px] text-zinc-400 font-semibold mt-1">
                        Estadia: {format(new Date(b.check_in), "dd 'de' MMM", { locale: getDateLocale() })} — {format(new Date(b.check_out), "dd 'de' MMM yyyy", { locale: getDateLocale() })}
                      </p>
                      <p className="text-[10px] text-zinc-450 font-bold mt-0.5">
                        Hóspede: {b.guests?.full_name} • E-mail: {b.guests?.email || "Sem e-mail"}
                      </p>
                    </div>
                    <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 dark:text-emerald-400 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Confirmada
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                Sem estadias confirmadas registradas no calendário.
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderMensagens = () => {
      return (
        <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm flex flex-col h-[650px]">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-zinc-800/40 pb-5 shrink-0">
            <div>
              <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.75", "Centro de Mensagens (Bera)")}</h4>
              <p className="text-[11px] text-zinc-400">{tAuto("auto.pages_explorar.76", "Bate-papo em tempo real com os hóspedes de seus espaços")}</p>
            </div>
            {unreadMessagesCount > 0 && (
              <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold animate-bounce">
                {unreadMessagesCount} novas
              </span>
            )}
          </div>
          
          <div className="flex-1 min-h-0 relative mt-4">
            <BeraAIChat onClose={() => setActiveMenu("dashboard")} />
          </div>
        </div>
      );
    };

    const renderFinanceiro = () => {
      const getFilteredTransactions = () => {
        if (!hostPaymentHistory) return [];
        let filtered = [...hostPaymentHistory];
        if (dateFrom) {
          filtered = filtered.filter(b => new Date(b.updated_at || b.created_at) >= new Date(dateFrom));
        }
        if (dateTo) {
          filtered = filtered.filter(b => new Date(b.updated_at || b.created_at) <= new Date(dateTo));
        }
        return filtered;
      };

      const transactions = getFilteredTransactions();
      const currentAvailableBalance = hostBalanceData?.available_balance || 0;
      const currentAccountingBalance = hostBalanceData?.accounting_balance || 0;
      
      const inProcessingWithdrawals = hostWithdrawals
        ? hostWithdrawals
            .filter((w: any) => w.status === "processing")
            .reduce((sum: number, w: any) => sum + (w.amount || 0), 0)
        : 0;

      return (
        <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-8 shadow-sm space-y-8">
          {/* Wallet stats headers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Saldo Disponível */}
            <div className="p-6 rounded-[24px] border border-gray-100 dark:border-zinc-800/40 bg-white dark:bg-[#1C1C1E] flex flex-col justify-between min-h-[160px] shadow-sm">
              <div>
                <span className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block">{tAuto("auto.pages_explorar.77", "Saldo Disponível")}</span>
                <h4 className="text-[32px] font-bold font-serif text-zinc-900 dark:text-zinc-100 mt-2">
                  {currentAvailableBalance.toLocaleString("pt-AO")} Kz
                </h4>
              </div>
              <Button
                disabled={currentAvailableBalance < 100000}
                onClick={() => setShowWithdrawDialog(true)}
                className="mt-6 w-full h-10 rounded-full bg-[#F59E90] hover:bg-primary disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-extrabold transition-all shadow-sm shrink-0"
              >
                Solicitar Saque
              </Button>
            </div>

            {/* Saldo Contabilístico */}
            <div className="p-6 rounded-[24px] border border-gray-100 dark:border-zinc-800/40 bg-white dark:bg-[#1C1C1E] flex flex-col justify-between min-h-[160px] shadow-sm">
              <div>
                <span className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block">{tAuto("auto.pages_explorar.78", "Saldo Contabilístico")}</span>
                <h4 className="text-[32px] font-bold font-serif text-zinc-900 dark:text-zinc-100 mt-2">
                  {currentAccountingBalance.toLocaleString("pt-AO")} Kz
                </h4>
              </div>
              <p className="text-[11px] text-zinc-450 dark:text-zinc-500 font-medium mt-3 leading-relaxed">
                Ganhos acumulados que estão em período de processamento ou segurança de reservas futuras.
              </p>
            </div>

            {/* Saques em Processamento */}
            <div className="p-6 rounded-[24px] border border-gray-100 dark:border-zinc-800/40 bg-white dark:bg-[#1C1C1E] flex flex-col justify-between min-h-[160px] shadow-sm">
              <div>
                <span className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest block">Saques em Processamento</span>
                <h4 className="text-[32px] font-bold font-serif text-amber-500 mt-2">
                  {inProcessingWithdrawals.toLocaleString("pt-AO")} Kz
                </h4>
              </div>
              <p className="text-[11px] text-zinc-450 dark:text-zinc-500 font-medium mt-3 leading-relaxed">
                Retiradas que já foram solicitadas e estão sendo enviadas ao seu IBAN por nossa equipe.
              </p>
            </div>
          </div>

          {/* Filtering bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-4 border-t border-gray-100 dark:border-zinc-800/40">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-zinc-500 dark:text-zinc-400 shrink-0">Filtrar por data:</span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 px-4 rounded-full border border-gray-200 dark:border-zinc-800 bg-white dark:bg-[#151518] text-xs font-medium text-zinc-700 dark:text-zinc-350 focus:outline-none focus:border-primary/40 w-36 text-center select-none"
                />
                <span className="text-zinc-300 dark:text-zinc-700 font-medium">—</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 px-4 rounded-full border border-gray-200 dark:border-zinc-800 bg-white dark:bg-[#151518] text-xs font-medium text-zinc-700 dark:text-zinc-350 focus:outline-none focus:border-primary/40 w-36 text-center select-none"
                />
                {(dateFrom || dateTo) && (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => { setDateFrom(""); setDateTo(""); }}
                    className="h-8 text-xs text-primary hover:text-[#D14430] hover:bg-transparent font-bold"
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  if (!transactions.length) {
                    toast({
                      title: tAuto("auto.pages_explorar.79", "Sem transações"),
                      description: tAuto("auto.pages_explorar.80", "Nenhuma transação disponível para exportar no período selecionado.")
                    });
                    return;
                  }
                  exportToCSV(
                    transactions.map((b: any) => ({
                      Data: format(new Date(b.updated_at || b.created_at), "dd/MM/yyyy"),
                      Propriedade: b.properties?.title || "—",
                      Hóspede: b.guests?.full_name || "—",
                      Valor: `${b.host_payout?.toLocaleString("pt-AO")} Kz`,
                      Estado: b.status === "completed" ? "Concluída" : b.status === "cancelled" ? "Cancelada" : "Confirmada",
                    })),
                    `extrato-berastays-${format(new Date(), "yyyy-MM-dd")}`
                  );
                  toast({
                    title: "CSV Exportado",
                    description: tAuto("auto.pages_explorar.81", "O seu extrato financeiro em CSV foi baixado com sucesso.")
                  });
                }}
                className="h-9 text-[11px] font-bold rounded-xl gap-1.5 border-gray-200 hover:bg-gray-50 dark:border-zinc-800 dark:hover:bg-zinc-800/40 text-zinc-700 dark:text-zinc-300 bg-white dark:bg-[#151518]"
              >
                <Download className="w-3.5 h-3.5 text-zinc-550 dark:text-zinc-400" />
                Exportar CSV
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  if (!transactions.length) {
                    toast({
                      title: tAuto("auto.pages_explorar.82", "Sem transações"),
                      description: tAuto("auto.pages_explorar.83", "Nenhuma transação disponível para exportar no período selecionado.")
                    });
                    return;
                  }
                  exportToPDF(
                    "Extrato de Faturamento",
                    transactions.map((b: any) => ({
                      data: format(new Date(b.updated_at || b.created_at), "dd/MM/yyyy"),
                      propriedade: b.properties?.title || "—",
                      hospede: b.guests?.full_name || "—",
                      valor: `${b.host_payout?.toLocaleString("pt-AO")} Kz`,
                      estado: b.status === "completed" ? "Concluída" : b.status === "cancelled" ? "Cancelada" : "Confirmada",
                    })),
                    `extrato-berastays-${format(new Date(), "yyyy-MM-dd")}`,
                    {
                      subtitle: dateFrom && dateTo 
                        ? `Período: ${format(new Date(dateFrom + "T00:00:00"), "dd/MM/yyyy")} a ${format(new Date(dateTo + "T00:00:00"), "dd/MM/yyyy")}` 
                        : "Todas as transações registradas",
                      columns: [
                        { key: "data", label: "Data" },
                        { key: "propriedade", label: "Propriedade / Alojamento" },
                        { key: "hospede", label: "Hóspede" },
                        { key: "valor", label: "Ganhos Líquidos" },
                        { key: "estado", label: "Status" },
                      ],
                    }
                  );
                  toast({
                    title: "PDF Exportado",
                    description: tAuto("auto.pages_explorar.84", "O seu extrato financeiro em PDF foi gerado com sucesso.")
                  });
                }}
                className="h-9 text-[11px] font-bold rounded-xl gap-1.5 border-gray-200 hover:bg-gray-50 dark:border-zinc-800 dark:hover:bg-zinc-800/40 text-zinc-700 dark:text-zinc-300 bg-white dark:bg-[#151518]"
              >
                <Download className="w-3.5 h-3.5 text-zinc-550 dark:text-zinc-400" />
                Exportar PDF
              </Button>
            </div>
          </div>

          {/* Transactions List */}
          <div className="space-y-4 pt-2">
            <h5 className="text-sm font-bold text-zinc-800 dark:text-zinc-200 font-display">{tAuto("auto.pages_explorar.85", "Histórico de Transações")}</h5>
            {transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-zinc-800 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                      <th className="pb-3 pl-2">{tAuto("auto.pages_explorar.86", "Propriedade")}</th>
                      <th className="pb-3">Data</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3">{tAuto("auto.pages_explorar.87", "Valor Líquido (Payout)")}</th>
                      <th className="pb-3 text-right pr-2">Recibo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-zinc-850">
                    {transactions.map((t: any) => {
                      const payDate = t.updated_at || t.created_at;
                      return (
                        <tr key={t.id} className="text-xs hover:bg-gray-50/20 dark:hover:bg-zinc-800/10">
                          <td className="py-3.5 pl-2 font-bold text-gray-800 dark:text-zinc-200">
                            {t.properties?.title || "Reserva"}
                          </td>
                          <td className="py-3.5 text-zinc-500 font-medium">
                            {format(new Date(payDate), "dd MMM yyyy", { locale: getDateLocale() })}
                          </td>
                          <td className="py-3.5">
                            <span className={cn(
                              "px-2 py-0.5 text-[9px] font-bold rounded-full uppercase",
                              t.payment_status === "paid" ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400" : "bg-red-50 text-red-500 dark:bg-red-950/20 dark:text-red-400"
                            )}>
                              {t.payment_status === "paid" ? "Pago" : "Reembolsado"}
                            </span>
                          </td>
                          <td className="py-3.5 font-bold text-primary">
                            +{t.host_payout.toLocaleString("pt-AO")} Kz
                          </td>
                          <td className="py-3.5 text-right pr-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                const receiptData: any = {
                                  id: t.id,
                                  created_at: t.created_at,
                                  guest_name: t.guests?.full_name || "Hóspede",
                                  guest_email: "",
                                  guest_phone: "",
                                  check_in: t.check_in,
                                  check_out: t.check_out,
                                  total_amount: t.host_payout / 0.90, // approx original total
                                  host_payout: t.host_payout,
                                  service_fee: (t.host_payout / 0.90) * 0.10,
                                  payment_method: "BeraStays Wallet",
                                  payment_status: t.payment_status,
                                  property_title: t.properties?.title || "Imóvel",
                                  property_address: "",
                                  host_name: profile?.full_name || "Proprietário",
                                };
                                downloadReceiptPDF(receiptData);
                              }}
                              className="h-8 w-8 hover:bg-gray-150 rounded-lg text-zinc-500"
                              title="Baixar recibo"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-20 text-zinc-500 dark:text-zinc-400 text-sm font-medium">
                Nenhuma transação financeira registrada para este período.
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderDesempenho = () => {
      return (
        <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-6">
          <div>
            <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.88", "Análise de Desempenho")}</h4>
            <p className="text-[11px] text-zinc-450 font-semibold">{tAuto("auto.pages_explorar.89", "Acompanhe a atratividade de seus espaços na BeraStays")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="p-5 rounded-2xl border border-gray-150 bg-gray-55/25 text-center">
              <span className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-wider block">{tAuto("auto.pages_explorar.90", "Conversão Média")}</span>
              <h4 className="text-3xl font-black mt-2 text-primary">{simulatedConversion}%</h4>
              <p className="text-[10px] text-zinc-400 mt-2">{tAuto("auto.pages_explorar.91", "Cliques relativos a visualizações")}</p>
            </div>
            
            <div className="p-5 rounded-2xl border border-gray-150 bg-gray-55/25 text-center">
              <span className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-wider block">Views Totais</span>
              <h4 className="text-3xl font-black mt-2 text-zinc-800 dark:text-zinc-200">{simulatedViews}</h4>
              <p className="text-[10px] text-zinc-400 mt-2">{tAuto("auto.pages_explorar.92", "Visitas aos seus anúncios")}</p>
            </div>

            <div className="p-5 rounded-2xl border border-gray-150 bg-gray-55/25 text-center">
              <span className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-wider block">{tAuto("auto.pages_explorar.93", "Cliques em Reservar")}</span>
              <h4 className="text-3xl font-black mt-2 text-zinc-800 dark:text-zinc-200">{simulatedClicks}</h4>
              <p className="text-[10px] text-zinc-400 mt-2">{tAuto("auto.pages_explorar.94", "Intenções reais de reservas")}</p>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-[#2A2A2A]">
            <h5 className="text-xs font-bold text-zinc-700 dark:text-zinc-300">Como Otimizar Seus Resultados</h5>
            <div className="space-y-3">
              {[
                { title: "Melhore as fotografias", desc: "Anúncios com fotos de alta qualidade e bem iluminadas ganham até 3x mais cliques." },
                { title: tAuto("auto.pages_explorar.95", "Preço competitivo"), desc: "Compare o preço de seu espaço com opções similares na mesma localidade." },
                { title: tAuto("auto.pages_explorar.96", "Responda rápido"), desc: "Manter o tempo de resposta do bate-papo abaixo de 1 hora aumenta a conversão em 45%." },
              ].map((tip, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-primary/[0.02] border border-orange-100/20 flex gap-3 items-start animate-fade-in">
                  <div className="w-6 h-6 rounded-lg bg-orange-50 text-primary flex items-center justify-center font-bold text-xs shrink-0">{idx + 1}</div>
                  <div>
                    <h6 className="text-xs font-bold text-zinc-800 dark:text-zinc-200">{tip.title}</h6>
                    <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const renderAvaliacoes = () => {
      return (
        <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-6">
          <div>
            <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.97", "Avaliações dos Hóspedes")}</h4>
            <p className="text-[11px] text-zinc-450">{tAuto("auto.pages_explorar.98", "Veja o que as pessoas dizem sobre suas acomodações")}</p>
          </div>

          {hostReviews && hostReviews.length > 0 ? (
            <div className="space-y-4">
              {hostReviews.map((r: any) => (
                <div key={r.id} className="border border-gray-150 dark:border-zinc-800/40 rounded-2xl p-4 space-y-2 bg-gray-55/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-primary text-white text-xs font-bold">
                          {r.guest_name ? r.guest_name.charAt(0) : "H"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h6 className="text-xs font-bold text-zinc-800 dark:text-zinc-200">{r.guest_name || "Hóspede"}</h6>
                        <span className="text-[9px] text-zinc-400 font-semibold">
                          {format(new Date(r.created_at), "dd MMM yyyy", { locale: getDateLocale() })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={cn(
                            "w-3.5 h-3.5 shrink-0",
                            i < (r.rating || 0) ? "text-zinc-900 fill-zinc-900 dark:text-zinc-100 dark:fill-zinc-100" : "text-gray-255"
                          )}
                        />
                      ))}
                    </div>
                  </div>
                  {r.comment && (
                    <p className="text-[11px] text-zinc-600 dark:text-zinc-400 italic leading-relaxed pl-1">
                      "{r.comment}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-400 border border-dashed border-gray-200 dark:border-zinc-800 rounded-3xl bg-gray-55/5">
              <Star className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
              <h5 className="font-bold text-xs">{tAuto("auto.pages_explorar.99", "Sem avaliações recebidas")}</h5>
              <p className="text-[11px] text-zinc-400 max-w-xs mx-auto mt-1 leading-relaxed">
                As avaliações de hóspedes que concluíram estadias nos seus espaços aparecerão listadas nesta seção.
              </p>
            </div>
          )}
        </div>
      );
    };

    const renderPedidosOferta = () => {
      return (
        <div className="bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl p-6 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-zinc-800/40 pb-5">
            <div>
              <h4 className="font-bold text-gray-800 dark:text-zinc-100 text-sm">{tAuto("auto.pages_explorar.100", "Pedidos de Oferta")}</h4>
              <p className="text-[11px] text-zinc-400">{tAuto("auto.pages_explorar.101", "Pedidos de estadia broadcasted por hóspedes na sua região")}</p>
            </div>
            <Button
              variant="outline"
              size="xs"
              onClick={() => refetchHostOffers()}
              className="h-8 text-xs font-bold rounded-xl border-gray-200 hover:bg-gray-50"
            >
              Atualizar Inbox
            </Button>
          </div>

          {hostOfferNotifications.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hostOfferNotifications.map((notif: any) => {
                const offer = notif.offer;
                if (!offer) return null;
                
                return (
                  <div key={notif.id} className="border border-gray-150 dark:border-zinc-800/40 rounded-2xl p-4 flex flex-col justify-between gap-4 bg-gray-50/10 hover:border-primary/20 transition-all">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-[9px] font-bold text-primary bg-red-50 dark:bg-red-950/20 px-2 py-0.5 rounded-full uppercase">
                          Região: {offer.province}
                        </span>
                        {offer.status === "pending" && (
                          <span className="text-[9px] font-bold text-amber-500 animate-pulse">
                            Ativa
                          </span>
                        )}
                      </div>
                      
                      <h5 className="font-bold text-xs text-zinc-700 dark:text-zinc-200">
                        Pedido de {offer.guest?.full_name || "Hóspede"} para {offer.property_type === "apartment" ? "Apartamento" : "Casa"}
                      </h5>
                      
                      <div className="space-y-1.5 mt-2.5 text-[10px] text-zinc-400 font-semibold">
                        <p>Noites: {offer.nights || 0} noites</p>
                        <p>Preço máximo: {offer.max_price_per_night.toLocaleString("pt-AO")} Kz/noite</p>
                        <p>Quartos: {offer.bedrooms} • WC: {offer.bathrooms}</p>
                        {offer.notes && <p className="italic text-zinc-400 mt-1">"{offer.notes}"</p>}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-zinc-800/40">
                      <Button
                        size="xs"
                        onClick={() => setReviewNotification(notif)}
                        className="flex-1 h-8 bg-primary hover:bg-[#D14430] text-white text-[11px] font-bold rounded-xl"
                      >
                        Analisar & Responder
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => handleRejectOffer(notif.id)}
                        className="h-8 text-[11px] font-bold rounded-xl border-gray-200 hover:bg-gray-50"
                      >
                        Dispensar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-400 border border-dashed border-gray-200 dark:border-zinc-800 rounded-3xl">
              Nenhum pedido de oferta broadcasted recebido para os seus locais até ao momento.
            </div>
          )}
        </div>
      );
    };

    const renderSeguro = () => {
      const STATS = [
        { label: "Total", value: insuranceStats.total },
        { label: "Ativos", value: insuranceStats.active },
        { label: "Curso", value: insuranceStats.pending }
      ];

      const INSURANCE_FILTERS = [
        { key: "all", label: "Todos" },
        { key: "not_activated", label: "Não ativado" },
        { key: "documents_pending", label: "Pendentes" },
        { key: "ready_to_apply", label: "Prontos" },
        { key: "submitted", label: "Enviados" },
        { key: "in_review", label: "Em análise" },
        { key: "active", label: "Ativos" },
        { key: "expired", label: "Expirados" },
      ];

      return (
        <div className="space-y-6">
          {/* Hero Header Banner */}
          <div className="relative overflow-hidden rounded-[24px] bg-gradient-to-r from-primary via-[#D14430] to-[#B83A28] p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-5 text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-300/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="flex items-center gap-3.5 z-10">
              <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold font-display leading-tight">{tAuto("auto.pages_explorar.102", "Meu Seguro")}</h3>
                <p className="text-xs text-white/80 font-medium mt-0.5">{tAuto("auto.pages_explorar.103", "Proteja os seus imóveis")}</p>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-3 z-10">
              {STATS.map((stat, idx) => (
                <div key={idx} className="min-w-[4.5rem] rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 p-2.5 text-center shadow-sm">
                  <div className="text-lg md:text-xl font-bold text-white tabular-nums leading-none">{stat.value}</div>
                  <div className="text-[9px] uppercase tracking-wider text-white/70 font-bold mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Filters + Search bar */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-80 shrink-0">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder={tAuto("auto.pages_explorar.104", "Pesquisar imóvel ou cidade…")}
                value={insuranceSearch}
                onChange={(e) => setInsuranceSearch(e.target.value)}
                className="pl-10 h-11 bg-white dark:bg-[#151518] border-gray-200/50 dark:border-zinc-800/40 rounded-2xl text-xs font-medium placeholder-zinc-400"
              />
            </div>

            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 w-full md:w-auto justify-start md:justify-end">
              {INSURANCE_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setInsuranceFilter(f.key)}
                  className={cn(
                    "shrink-0 px-4 py-2 rounded-2xl text-xs font-bold transition-all border",
                    insuranceFilter === f.key
                      ? "bg-primary text-white border-primary shadow-sm"
                      : "bg-white dark:bg-[#151518] text-zinc-600 dark:text-zinc-400 border-gray-200/50 dark:border-zinc-800/40 hover:border-primary/40 hover:text-primary dark:hover:text-primary"
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Requests list grid */}
          {isInsuranceLoading ? (
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 rounded-3xl h-64" />
              ))}
            </div>
          ) : filteredInsurance.length === 0 ? (
            /* Empty state */
            <div className="rounded-3xl bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 p-10 md:p-14 text-center shadow-sm max-w-4xl mx-auto w-full">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-amber-100/10 flex items-center justify-center mx-auto mb-4 border border-primary/10">
                <ShieldAlert className="w-7 h-7 text-primary" strokeWidth={1.75} />
              </div>
              <h4 className="text-base font-bold text-gray-800 dark:text-zinc-200 mb-2">
                {enrichedInsurance.length > 0 ? "Sem resultados nesta categoria" : "Ainda sem imóveis em seguro"}
              </h4>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 max-w-md mx-auto mb-6 leading-relaxed">
                {enrichedInsurance.length > 0
                  ? "Ajuste os filtros ou pesquise por outro imóvel."
                  : "Quando publicar um anúncio, marque a opção de seguro no último passo. O imóvel aparecerá aqui automaticamente."}
              </p>
              {enrichedInsurance.length === 0 && (
                <Button onClick={() => setActiveMenu("imoveis")} className="bg-primary hover:bg-[#D14430] text-white text-xs font-bold px-5 h-10 rounded-xl shadow-sm">
                  Ir para os meus imóveis
                </Button>
              )}
            </div>
          ) : (
            /* Request cards */
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredInsurance.map((r: any) => {
                const status = r.effectiveStatus;
                const color = STATUS_COLOR[status] || { bg: "bg-zinc-100", text: "text-zinc-750", border: "border-zinc-200", dot: "bg-zinc-400" };
                const property = r.property;
                const img = getFirstImageUrl(property?.images);
                const ds = daysUntil(r.end_date);
                const monthlyEstimate = property ? Math.round(property.price_per_night * 30 * 0.8) : 0;

                return (
                  <motion.div
                    key={r.id}
                    whileHover={{ y: -4 }}
                    className="bg-white dark:bg-[#151518] rounded-3xl border border-gray-200/50 dark:border-zinc-800/40 overflow-hidden shadow-sm hover:shadow-md transition-all group flex flex-col justify-between"
                  >
                    <div>
                      {/* Image section */}
                      <div className="relative aspect-[16/10] bg-zinc-100 dark:bg-zinc-900 overflow-hidden">
                        <OptimizedImage src={img} alt={property?.title || "Imóvel"} aspectRatio="16/9" />
                        
                        {/* Status Badge overlay */}
                        <div className={cn(
                          "absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-extrabold border backdrop-blur-md uppercase tracking-wider",
                          color.bg, color.text, color.border
                        )}>
                          <span className={cn("w-1.5 h-1.5 rounded-full", color.dot)} />
                          {STATUS_LABEL[status]}
                        </div>

                        {property?.insurance_badge_active && (
                          <div className="absolute top-3 right-3 z-10">
                            <InsuredBadge size="sm" />
                          </div>
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="p-5 space-y-3.5">
                        <div>
                          <h5 className="font-bold text-gray-800 dark:text-zinc-150 text-sm line-clamp-1 group-hover:text-primary transition-colors">
                            {property?.title || "Imóvel"}
                          </h5>
                          <div className="flex items-center gap-1.5 text-[10.5px] text-zinc-400 mt-1 font-semibold">
                            <MapPin className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            <span className="truncate">{property?.city}, {property?.province}</span>
                          </div>
                        </div>

                        <p className="text-[11px] text-zinc-500 dark:text-zinc-400 line-clamp-2 leading-relaxed">
                          {property?.description || "Sem descrição disponível."}
                        </p>

                        {/* Capacity / Info rows */}
                        <div className="flex items-center gap-4 text-[10px] text-zinc-500 font-bold border-t border-gray-100/50 dark:border-zinc-800/20 pt-2.5">
                          <span className="inline-flex items-center gap-1"><Bed className="w-3.5 h-3.5 text-zinc-400" />{property?.bedrooms ?? "–"} quartos</span>
                          <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5 text-zinc-400" />{property?.bathrooms ?? "–"} WCs</span>
                        </div>

                        {/* Pricing details */}
                        <div className="grid grid-cols-2 gap-3 bg-gray-50/50 dark:bg-zinc-800/10 rounded-2xl border border-gray-100/50 dark:border-zinc-800/20 p-3">
                          <div>
                            <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider block">Por noite</span>
                            <span className="text-xs font-bold text-gray-800 dark:text-zinc-200 mt-0.5 block">{fmt(property?.price_per_night)}</span>
                          </div>
                          <div>
                            <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider block">Estim. mensal</span>
                            <span className="text-xs font-bold text-gray-800 dark:text-zinc-200 mt-0.5 block">{fmt(monthlyEstimate)}</span>
                          </div>
                        </div>

                        {/* Active Policy info card */}
                        {status === "active" && (
                          <div className="rounded-2xl border border-primary/20 bg-primary/[0.02] p-3 space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[9px] font-extrabold uppercase tracking-wider text-[#C2493A]">{tAuto("auto.pages_explorar.105", "Apólice ativa")}</span>
                              {ds !== null && ds >= 0 && (
                                <span className="text-[9px] font-bold text-zinc-500">Termina em {ds} dias</span>
                              )}
                            </div>
                            <div className="flex items-center justify-between text-[11px] text-zinc-700 dark:text-zinc-300 font-semibold">
                              <span>{tAuto("auto.pages_explorar.106", "Prémio:")} <span className="font-bold text-gray-800 dark:text-white">{fmt(r.premium_amount)}</span></span>
                              <span>{tAuto("auto.pages_explorar.107", "Válido até:")} <span className="font-bold text-gray-800 dark:text-white">{formatDate(r.end_date)}</span></span>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 font-semibold pt-1">
                          <CalendarDays className="w-3.5 h-3.5" />
                          <span>Solicitado em {formatDate(r.requested_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action footer */}
                    <div className="px-5 pb-5 pt-3.5 border-t border-gray-100/50 dark:border-zinc-800/20 bg-gray-50/20 dark:bg-zinc-900/5 flex items-center justify-between shrink-0">
                      <span className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300">
                        {status === "ready_to_apply" ? (
                          <span className="inline-flex items-center gap-1 text-primary"><Send className="w-3.5 h-3.5" /> {tAuto("auto.pages_explorar.108", "Pronto a enviar")}</span>
                        ) : status === "documents_pending" || status === "not_activated" || status === "docs_required" ? (
                          <span className="inline-flex items-center gap-1 text-amber-600"><FileImage className="w-3.5 h-3.5" /> Carregar fotos</span>
                        ) : status === "in_review" || status === "submitted" ? (
                          <span className="inline-flex items-center gap-1 text-primary"><CoralPin className="w-3.5 h-3.5" /> {tAuto("auto.pages_explorar.109", "Aguardar análise")}</span>
                        ) : (
                          "Ver detalhes"
                        )}
                      </span>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => navigate(`/seguro/imovel/${r.id}`)}
                        className="text-xs font-bold text-primary hover:text-[#D14430] hover:bg-transparent p-0 gap-1 animate-pulse-subtle"
                      >
                        Abrir <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Insurance Coverage info panel */}
          <div className="mt-8">
            <h4 className="text-sm font-bold text-gray-800 dark:text-zinc-200 mb-3.5">{tAuto("auto.pages_explorar.110", "Cobertura do Seguro")}</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white dark:bg-[#151518] border border-emerald-100 dark:border-emerald-950/20 p-5 space-y-3.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-4 h-4 text-emerald-650" strokeWidth={2.5} />
                  </div>
                  <span className="font-bold text-xs text-emerald-800 dark:text-emerald-400">Pode estar coberto</span>
                </div>
                <ul className="space-y-2">
                  {DEFAULT_COVERAGE_INCLUDED.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-xs text-zinc-600 dark:text-zinc-400 leading-snug">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl bg-white dark:bg-[#151518] border border-rose-100 dark:border-rose-950/20 p-5 space-y-3.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-rose-50 dark:bg-rose-950/30 flex items-center justify-center shrink-0">
                    <ShieldAlert className="w-4 h-4 text-rose-650" strokeWidth={2.5} />
                  </div>
                  <span className="font-bold text-xs text-rose-800 dark:text-rose-455">{tAuto("auto.pages_explorar.111", "Não está coberto")}</span>
                </div>
                <ul className="space-y-2">
                  {DEFAULT_COVERAGE_EXCLUDED.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-xs text-zinc-600 dark:text-zinc-400 leading-snug">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Support Section card */}
          <div className="rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-850 to-zinc-950 text-white p-5 border border-zinc-800 mt-6 shadow-sm z-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-36 h-36 bg-white/5 rounded-full blur-2xl pointer-events-none" />
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h5 className="text-sm font-bold">{tAuto("auto.pages_explorar.112", "Precisa de ajuda com o seu seguro?")}</h5>
                <p className="text-xs text-white/70 mt-0.5">
                  Equipa BeraStays Insurance disponível para esclarecer dúvidas.
                </p>
              </div>
              <div className="flex flex-wrap gap-2.5 shrink-0">
                <a
                  href={`mailto:${SUPPORT.email}`}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 transition-colors text-xs font-bold border border-white/10"
                >
                  <Mail className="w-3.5 h-3.5" /> Email
                </a>
                <a
                  href={`https://wa.me/${SUPPORT.whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-primary hover:bg-[#D14430] transition-colors text-xs font-extrabold"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="min-h-screen bg-[#F8F9FA] dark:bg-[#0C0C0E] text-foreground flex">
        {/* Left Sidebar */}
        <aside className="w-[260px] bg-white dark:bg-[#151518] border-r border-gray-200/50 dark:border-zinc-800/40 flex flex-col fixed top-0 bottom-0 left-0 z-30">
          {/* Logo block */}
          <div className="flex items-center gap-3 px-6 h-20 border-b border-gray-100 dark:border-zinc-800/40 shrink-0 font-sans">
            <img src={beraLogo} alt="BeraStays" className="h-9 w-auto" />
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1 font-sans">
            {[
              { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
              { id: "imoveis", label: "Imóveis", icon: Home },
              { id: "reservas", label: "Reservas", icon: CalendarCheck },
              { id: "financeiro", label: "Financeiro", icon: Wallet },
              { id: "desempenho", label: "Desempenho", icon: TrendingUp },
              { id: "avaliacoes", label: "Avaliações", icon: Star },
              { id: "pedidos_oferta", label: "Pedidos de oferta", icon: FileText, badge: hostOfferNotifications.filter((n: any) => !n.is_read).length },
              { id: "seguro", label: "Seguro", icon: ShieldCheck },
            ].map((item) => {
              const Icon = item.icon;
              const isActive = activeMenu === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveMenu(item.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3.5 py-3 rounded-xl text-sm font-semibold transition-all duration-200",
                    isActive
                      ? "bg-primary text-white shadow-md shadow-primary/10"
                      : "text-zinc-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800/50 hover:text-zinc-955 dark:hover:text-zinc-100"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && item.badge > 0 ? (
                    <span className={cn(
                      "px-2 py-0.5 text-[10px] font-bold rounded-full",
                      isActive ? "bg-white text-primary" : "bg-red-500 text-white"
                    )}>
                      {item.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </nav>

          {/* Settings / Profile Footer */}
          <div className="p-4 border-t border-gray-100 dark:border-zinc-800/40 shrink-0 space-y-3 font-sans">
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setActiveMenu("configuracoes")}
                className={cn(
                  "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-semibold transition-all",
                  activeMenu === "configuracoes"
                    ? "bg-primary text-white shadow-md shadow-primary/10"
                    : "text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800/50 hover:text-foreground"
                )}
              >
                <Settings className={cn("w-4 h-4", activeMenu === "configuracoes" ? "text-white" : "text-zinc-400")} />
                Configurações
              </button>

              <button
                onClick={async () => {
                  await signOut();
                  navigate("/", { replace: true, state: { fromLogout: true } });
                }}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-xs font-semibold text-zinc-500 hover:bg-red-50 dark:hover:bg-red-950/20 hover:text-primary transition-all group"
              >
                <LogOut className="w-4 h-4 text-zinc-400 group-hover:text-primary transition-colors" />
                Sair
              </button>
            </div>
            
            {/* User Profile Block */}
            <div
              onClick={() => setActiveMenu("perfil")}
              className={cn(
                "flex items-center justify-between p-2 rounded-xl border transition-all cursor-pointer",
                activeMenu === "perfil"
                  ? "bg-primary text-white border-primary shadow-md shadow-primary/10"
                  : "bg-gray-50 dark:bg-zinc-800/30 border-gray-100 dark:border-zinc-800/30 hover:bg-gray-100 dark:hover:bg-zinc-800/50"
              )}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar className="w-9 h-9 border-2 border-white dark:border-zinc-800">
                  <AvatarImage src={profile?.avatar_url || user?.user_metadata?.avatar_url} />
                  <AvatarFallback className={cn("text-xs font-bold text-white", activeMenu === "perfil" ? "bg-white/25" : "bg-gradient-to-br from-primary to-[#D14430]")}>
                    {ownerFirstName ? ownerFirstName.charAt(0) : "P"}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className={cn(
                    "text-xs font-bold truncate leading-none",
                    activeMenu === "perfil" ? "text-white" : "text-zinc-800 dark:text-zinc-200"
                  )}>
                    {profile?.full_name || user?.user_metadata?.full_name || "Proprietário"}
                  </p>
                  <span className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider block mt-0.5 leading-none",
                    activeMenu === "perfil" ? "text-white/70" : "text-zinc-400 dark:text-zinc-500"
                  )}>
                    Proprietário
                  </span>
                </div>
              </div>
              <ChevronDown className={cn("w-4 h-4 shrink-0", activeMenu === "perfil" ? "text-white" : "text-zinc-400")} />
            </div>
          </div>
        </aside>

        {/* Content Area */}
        <div className="flex-1 pl-[260px] min-h-screen flex flex-col font-sans">
          {/* Header */}
          <header className="sticky top-0 z-20 h-20 bg-white/80 dark:bg-[#151518]/80 backdrop-blur-md border-b border-gray-200/40 dark:border-zinc-800/30 px-8 flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-zinc-850 dark:text-zinc-100 font-display flex items-center gap-1.5">
                Olá, {ownerFirstName || "Proprietário"} 👋
              </h1>
              <p className="text-xs text-zinc-400 dark:text-zinc-500 font-medium mt-0.5">
                Gerencie e acompanhe a atividade de seus espaços.
              </p>
            </div>
            
            {/* Quick header action buttons */}
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveMenu("financeiro")}
                className="h-9 px-4 border-gray-200 text-xs font-bold rounded-xl gap-2 hover:bg-gray-50 shrink-0"
              >
                <Wallet className="w-4 h-4 text-zinc-500" />
                Financeiro
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/anunciar")}
                className="h-9 px-4 bg-primary hover:bg-[#D14430] text-white text-xs font-bold rounded-xl gap-2 shadow-sm shrink-0"
              >
                <Plus className="w-4.5 h-4.5" />
                Adicionar imóvel
              </Button>
            </div>
          </header>

          <main className="flex-1 p-8 space-y-6 overflow-y-auto max-w-[1400px] w-full mx-auto">
            {/* Status Info Banner */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-gray-50/50 dark:bg-zinc-800/20 border border-gray-150 dark:border-zinc-800/40 text-xs text-zinc-500 dark:text-zinc-400 font-semibold select-none">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span>Hoje:</span>
              <span className="text-zinc-700 dark:text-zinc-300">{todayCheckIns} check-ins</span>
              <span className="text-zinc-300 dark:text-zinc-700">•</span>
              <span className="text-zinc-700 dark:text-zinc-300">{todayCheckOuts} check-outs</span>
              <span className="text-zinc-300 dark:text-zinc-700">•</span>
              <span className="text-zinc-700 dark:text-zinc-300">{pendingBookingsCount} reservas pendentes</span>
            </div>

            {/* Render Active View */}
            {activeMenu === "dashboard" && renderDashboard()}
            {activeMenu === "imoveis" && renderImoveis()}
            {activeMenu === "reservas" && renderReservas()}
            {activeMenu === "mensagens" && renderMensagens()}
            {activeMenu === "financeiro" && renderFinanceiro()}
            {activeMenu === "desempenho" && renderDesempenho()}
            {activeMenu === "avaliacoes" && renderAvaliacoes()}
            {activeMenu === "pedidos_oferta" && renderPedidosOferta()}
            {activeMenu === "seguro" && renderSeguro()}
            {activeMenu === "configuracoes" && <HostSettings />}
            {activeMenu === "perfil" && <HostProfile />}
          </main>
        </div>

        {/* Withdraw Request Dialog */}
        <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-base font-bold">{tAuto("auto.pages_explorar.113", "Solicitar Saque de Saldo")}</DialogTitle>
              <DialogDescription className="text-xs">
                Insira o valor que deseja transferir para o seu IBAN cadastrado ({profile?.iban || "Não configurado"}).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-zinc-550 block">{tAuto("auto.pages_explorar.114", "Valor do Saque (mínimo 100.000 Kz)")}</label>
                <div className="relative">
                  <Input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder="Ex: 50000"
                    className="h-11 pl-4 pr-12 text-sm font-bold"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-zinc-400">
                    Kz
                  </span>
                </div>
                <p className="text-[10px] text-zinc-450 font-semibold">
                  Saldo disponível: {(hostBalanceData?.available_balance || 0).toLocaleString("pt-AO")} Kz
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0 border-t border-gray-100 dark:border-zinc-800/40 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowWithdrawDialog(false)}
                className="h-9 rounded-xl text-xs font-bold border-gray-250"
              >
                Cancelar
              </Button>
              <Button
                disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) < 100000 || parseFloat(withdrawAmount) > (hostBalanceData?.available_balance || 0)}
                onClick={handleWithdraw}
                className="h-9 rounded-xl text-xs font-bold bg-primary hover:bg-[#D14430] text-white"
              >
                {withdrawLoading ? "A enviar..." : "Confirmar Saque"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Property Confirmation Dialog */}
        <AlertDialog open={!!deletePropertyId} onOpenChange={(o) => { if (!o) setDeletePropertyId(null); }}>
          <AlertDialogContent className="rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-base font-bold">{tAuto("auto.pages_explorar.115", "Remover Anúncio de Imóvel")}</AlertDialogTitle>
              <AlertDialogDescription className="text-xs leading-relaxed">
                Tem a certeza que deseja eliminar permanentemente este anúncio de imóvel? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-0">
              <AlertDialogCancel className="h-9 rounded-xl text-xs font-bold">{tAuto("auto.pages_explorar.116", "Cancelar")}</AlertDialogCancel>
              <AlertDialogAction
                disabled={isDeleting}
                onClick={handleDeleteProperty}
                className="h-9 rounded-xl text-xs font-bold bg-red-500 hover:bg-red-650 text-white"
              >
                {isDeleting ? "A eliminar..." : "Eliminar Anúncio"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Booking Confirmation Dialog */}
        <AlertDialog open={!!deleteBookingId} onOpenChange={(o) => { if (!o) setDeleteBookingId(null); }}>
          <AlertDialogContent className="rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-base font-bold">{tAuto("auto.pages_explorar.117", "Eliminar Registo de Reserva")}</AlertDialogTitle>
              <AlertDialogDescription className="text-xs leading-relaxed">
                Deseja remover o registo desta reserva do seu painel? A reserva será permanentemente ocultada.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-0">
              <AlertDialogCancel className="h-9 rounded-xl text-xs font-bold">{tAuto("auto.pages_explorar.118", "Cancelar")}</AlertDialogCancel>
              <AlertDialogAction
                disabled={isDeletingBooking}
                onClick={handleDeleteBooking}
                className="h-9 rounded-xl text-xs font-bold bg-red-500 hover:bg-red-650 text-white"
              >
                {isDeletingBooking ? "A eliminar..." : "Eliminar Registo"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Property Dialog */}
        <EditPropertyDialog
          property={editProperty}
          open={!!editProperty}
          onOpenChange={(open) => { if (!open) setEditProperty(null); }}
          onSaved={() => refetchHostProperties()}
        />

        <ShareSpaceDialog
          open={!!shareProperty}
          onOpenChange={(o) => { if (!o) setShareProperty(null); }}
          propertyId={shareProperty?.id || ""}
          propertyTitle={shareProperty?.title}
        />

        {/* Offer broadcast Negotiation dialog */}
        <OfferReviewDialog
          notification={reviewNotification}
          open={!!reviewNotification}
          onClose={() => setReviewNotification(null)}
          onAccept={handleAcceptOffer}
          onReject={handleRejectOffer}
          onSendCounter={handleSendCounter}
          isSubmitting={isSubmittingOffer}
        />
      </div>
    );
  }

  const itemsPerPage = isMobile ? ITEMS_PER_MOBILE_PAGE : ITEMS_PER_PAGE;

  return (
    <div className={`flex flex-col ${isMobile ? "h-[100dvh] overflow-hidden bg-[#F8F9FA] dark:bg-[#121212]" : "min-h-screen bg-background"}`}>
      <Header />
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        url={`https://bera-stays.lovable.app/explorar${searchQuery ? `?q=${searchQuery}` : ""}`}
      />
      {/* Mobile Search Bar - Static above scrollable main, header is fixed */}
      {isMobile && (
        <div className="lg:hidden flex-shrink-0 bg-[#F8F9FA] dark:bg-[#121212] pt-[56px] px-3 pb-0 z-40">
          <MobileSearchBar
            variant={isHostOnly ? "host" : "guest"}
            onOpenFilters={() => setIsFilterOpen(true)}
            selectedCategory={filters.propertyType}
            onCategoryChange={(category) => setFilters(prev => ({ ...prev, propertyType: category }))}
          />
        </div>
      )}

      <main
        className={`flex-1 ${isMobile ? "overflow-y-auto pb-[calc(3.75rem+var(--safe-bottom))]" : "pt-24 pb-4"}`}
      >
        {isMobile ? (
          <div className="bg-transparent px-3 pb-0 pt-0 -mt-1">
            {isHostOnly ? (
              /* Host-only mobile dashboard header - aligned as per screenshot */
              <div className="flex items-center justify-between mb-1 min-h-[36px]">
                <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                  {hostOwnProperties > 0 && (
                    <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50/50 shadow-sm text-[12px] font-bold text-gray-800 shrink-0">
                      <BarChart3 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
                      <span>{hostOwnProperties} seus</span>
                    </div>
                  )}
                </div>
                <Select
                  value={sortBy}
                  onValueChange={(v) => setSortBy(v as SortOption)}
                >
                  <SelectTrigger className="h-9 w-auto gap-1 text-[13px] font-bold border-0 bg-transparent px-2 shadow-none hover:bg-gray-50 rounded-full transition-colors flex items-center shrink-0">
                    <ArrowUpDown className="w-[14px] h-[14px] text-primary" strokeWidth={2.5} />
                    <span className="text-gray-800">Ordenar</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500 ml-0.5" />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="bera_opportunity">
                      <span className="flex items-center gap-1.5 text-xs">
                        <span className="font-medium">BeraOpportunity</span>
                        <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-primary to-[#D14430] text-white text-[10px] font-semibold shadow-sm">justo</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="relevance">{tAuto("auto.pages_explorar.119", "Relevância")}</SelectItem>
                    <SelectItem value="price_asc">{tAuto("auto.pages_explorar.120", "Preço: Menor")}</SelectItem>
                    <SelectItem value="price_desc">{tAuto("auto.pages_explorar.121", "Preço: Maior")}</SelectItem>
                    <SelectItem value="rating">{tAuto("auto.pages_explorar.122", "Melhor Avaliação")}</SelectItem>
                    <SelectItem value="newest">Mais Recentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex items-center justify-end -mt-1 min-h-[36px]">
                <Select
                  value={sortBy}
                  onValueChange={(v) => setSortBy(v as SortOption)}
                >
                  <SelectTrigger className="h-9 w-auto gap-1.5 text-[13px] font-medium border-0 bg-transparent px-3 shadow-none hover:bg-gray-50 rounded-full transition-colors">
                    <ArrowUpDown className="w-[14px] h-[14px] text-primary" />
                    <span className="text-gray-800">Ordenar</span>
                  </SelectTrigger>
                  <SelectContent align="end" className="rounded-2xl border-gray-100 shadow-xl min-w-[200px]">
                    <SelectItem value="bera_opportunity">
                      <span className="flex items-center gap-1.5 text-xs">
                        <span className="font-medium">BeraOpportunity</span>
                        <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-primary to-[#D14430] text-white text-[10px] font-semibold shadow-sm">justo</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="relevance">{tAuto("auto.pages_explorar.123", "Relevância")}</SelectItem>
                    <SelectItem value="price_asc">{tAuto("auto.pages_explorar.124", "Preço: Menor")}</SelectItem>
                    <SelectItem value="price_desc">{tAuto("auto.pages_explorar.125", "Preço: Maior")}</SelectItem>
                    <SelectItem value="rating">{tAuto("auto.pages_explorar.126", "Melhor Avaliação")}</SelectItem>
                    <SelectItem value="newest">Mais Recentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <PropertyFilters
              filters={filters}
              onFiltersChange={setFilters}
              totalResults={sortedProperties.length}
              variant={isHostOnly ? "host" : "guest"}
              isOpen={isFilterOpen}
              onOpenChange={setIsFilterOpen}
            />
          </div>
        ) : (
          <section className="py-3">
            <div className="container mx-auto px-4">
              {isHostOnly ? (
                /* Host-only dashboard header - compact desktop */
                <div className="flex flex-col gap-6 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <h1 className="font-display text-xl lg:text-2xl font-bold">
                          Painel de <span className="text-gradient">{tAuto("auto.pages_explorar.127", "Espaços")}</span>
                        </h1>
                        <p className="text-muted-foreground text-xs mt-0.5">
                          Visualize e monitorise os espaços na plataforma
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="gap-1.5 text-xs h-8">
                        <Eye className="w-3 h-3" />
                        {sortedProperties.length} espaços
                      </Badge>
                      {hostOwnProperties > 0 && (
                        <Badge variant="outline" className="gap-1.5 text-xs h-8">
                          <BarChart3 className="w-3 h-3" />
                          {hostOwnProperties} seus
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Desktop Search Bar - Only for hosts */}
                  <div className="w-full flex justify-center">
                    <div className="w-full max-w-4xl">
                      <DesktopSearchBar variant="host" onOpenFilters={() => setIsFilterOpen(true)} />
                    </div>
                  </div>
                </div>
              ) : (
                /* Guest / default header - text left, search bar centered */
                <div className="mb-6 flex flex-col gap-6">
                  {/* Text - left aligned */}
                  <div className="text-left">
                    <h1 className="font-display text-3xl lg:text-4xl font-bold text-foreground">
                      Encontre o seu <span className="text-gradient">{tAuto("auto.pages_explorar.128", "espaço perfeito")}</span>
                    </h1>
                    <p className="text-muted-foreground text-sm lg:text-base mt-2">
                      África Vista de Perto
                    </p>
                  </div>
                  {/* Search Bar - centered */}
                  <div className="w-full flex justify-center">
                    <div className="w-full max-w-4xl">
                      <CompactSearchBar
                        onOpenFilters={() => setIsFilterOpen(true)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                totalResults={sortedProperties.length}
                variant={isHostOnly ? "host" : "guest"}
                isOpen={isFilterOpen}
                onOpenChange={setIsFilterOpen}
              />

              <div className="flex items-center justify-end mt-3">
                <Select
                  value={sortBy}
                  onValueChange={(v) => setSortBy(v as SortOption)}
                >
                  <SelectTrigger className="w-[240px] h-8 text-xs [&>span]:line-clamp-none [&>span]:flex [&>span]:items-center [&>span]:gap-2">
                    <ArrowUpDown className="w-3 h-3 mr-1.5" />
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bera_opportunity">
                      <span className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">BeraOpportunity</span>
                        <span className="px-2 py-0.5 rounded-md bg-gradient-to-r from-primary to-[#D14430] text-white text-[10px] font-semibold tracking-wide shadow-sm">JUSTO</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="relevance">{tAuto("auto.pages_explorar.129", "Relevância")}</SelectItem>
                    <SelectItem value="price_asc">{tAuto("auto.pages_explorar.130", "Preço: Menor")}</SelectItem>
                    <SelectItem value="price_desc">{tAuto("auto.pages_explorar.131", "Preço: Maior")}</SelectItem>
                    <SelectItem value="rating">{tAuto("auto.pages_explorar.132", "Melhor Avaliação")}</SelectItem>
                    <SelectItem value="newest">Mais Recentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
        )}

        <section className={isMobile ? "px-3 pb-3 pt-0" : "container mx-auto px-4 py-4"}>
          {isLoading ? (
            <div
              className={`grid gap-3 ${isMobile ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"}`}
            >
              {Array.from({ length: isMobile ? 6 : 10 }).map((_, i) => (
                <PropertyCardSkeleton key={i} />
              ))}
            </div>
          ) : displayedProperties.length > 0 ? (
            <>
              <div
                className={`grid gap-3 ${isMobile ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"}`}
              >
                {displayedProperties.map((property, idx) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    compact={isMobile}
                    priority={isMobile ? idx < 4 : idx < 2}
                  />
                ))}
              </div>

              {/* Infinite Scroll Loader - All versions */}
              <InfiniteScrollLoader
                isLoading={isLoadingMore}
                hasMore={currentPage < totalPages}
                isMobile={isMobile}
                loadMoreRef={loadMoreRef}
              />
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <MapPin className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-1">
                Nenhum imóvel encontrado
              </h3>
              <p className="text-muted-foreground text-sm mb-4 max-w-xs mx-auto">
                Tente ajustar os filtros
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ ...DEFAULT_FILTERS })}
              >
                Limpar Filtros
              </Button>
            </div>
          )}
        </section>

        {/* Desktop Pagination - Fixed at bottom */}
        {!isMobile && sortedProperties.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={sortedProperties.length}
            itemsPerPage={itemsPerPage}
          />
        )}
      </main>
    </div>
  );
};

/* ── Custom SVG Icons ── */
const PriceTagIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M2.5 9.5V3.5a1 1 0 011-1h6l6 6-6 6-6-6z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="6.5" cy="6.5" r="1" fill="currentColor" />
  </svg>
);

const CounterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4h12M2 8h8M2 12h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M12 10l2 2-2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 11V7a2 2 0 012-2h8a2 2 0 012 2v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M1 11V12.5M13 11V12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <rect x="3" y="3" width="3" height="2" rx="0.5" stroke="currentColor" strokeWidth="1" />
    <rect x="8" y="3" width="3" height="2" rx="0.5" stroke="currentColor" strokeWidth="1" />
  </svg>
);

const BathIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 7h10v2a3 3 0 01-3 3H5a3 3 0 01-3-3V7z" stroke="currentColor" strokeWidth="1.2" />
    <path d="M3 7V3.5a1.5 1.5 0 013 0V7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M3.5 12v1M10.5 12v1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M11.5 7.5a5 5 0 01-7-7 5 5 0 107 7z" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const HomeTypeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 7l5-4.5L12 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.5 8v4h3v-2.5h1v2.5h3V8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PROPERTY_LABELS: Record<string, string> = {
  apartment: "Apartamento", Apartamento: "Apartamento",
  house: "Casa", Casa: "Casa",
  Hotel: "Hotel", hotel: "Hotel",
  Hospedaria: "Hospedaria", hospedaria: "Hospedaria",
  "Casa de hóspedes": "Casa de hóspedes",
  Condomínio: "Condomínio", condominio: "Condomínio",
  "Contentor adaptado": "Contentor", contentor: "Contentor",
  Resort: "Resort", resort: "Resort",
  Barco: "Barco", barco: "Barco",
  room: "Quarto",
};

const PROPERTY_TYPES_LIST = PROPERTY_TYPE_VALUES;

const ALL_AMENITIES = [
  "wifi", "parking", "pool", "tv", "gym", "ac", "heating", "washer",
  "pets", "mountain_view", "garden", "breakfast", "cleaning", "living_room",
  "garage", "fridge", "microwave", "iron", "balcony", "safe", "doorbell",
  "first_aid", "concierge", "security", "hot_water", "kitchen", "streaming",
  "fenced", "elevator", "generator", "bbq", "workspace",
];

/* ── Review Item Component ── */
function ReviewItem({
  icon,
  label,
  guestValue,
  confirmed,
  onToggle,
}: {
  icon: React.ReactNode;
  label: string;
  guestValue: React.ReactNode;
  confirmed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 text-left ${
        confirmed
          ? "border-primary/30 bg-primary/[0.04]"
          : "border-border/50 bg-card hover:border-border"
      }`}
    >
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
        confirmed ? "bg-primary/10 text-primary" : "bg-muted/60 text-muted-foreground"
      }`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <div className="text-sm font-medium text-foreground truncate">{guestValue}</div>
      </div>
      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
        confirmed
          ? "bg-primary text-white scale-100"
          : "bg-muted/60 text-muted-foreground scale-90"
      }`}>
        {confirmed ? <Check className="w-3.5 h-3.5" /> : <div className="w-2.5 h-2.5 rounded-full border-2 border-current" />}
      </div>
    </button>
  );
}

/* ── NumberStepper ── */
function NumberStepper({ value, onChange, min = 1, max = 20 }: { value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-8 h-8 rounded-lg bg-muted/60 flex items-center justify-center hover:bg-muted transition-colors"
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <span className="w-8 text-center text-sm font-bold">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-8 h-8 rounded-lg bg-muted/60 flex items-center justify-center hover:bg-muted transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ── Review Dialog ── */
type ReviewStep = "review" | "counter";

function OfferReviewDialog({
  notification,
  open,
  onClose,
  onAccept,
  onReject,
  onSendCounter,
  isSubmitting,
}: {
  notification: any;
  open: boolean;
  onClose: () => void;
  onAccept: (notification: any) => void;
  onReject: (notificationId: string) => void;
  onSendCounter: (notification: any, data: any) => void;
  isSubmitting: boolean;
}) {
  const fmt = useFmt();
  const offer = notification?.offer;
  const property = notification?.property;
  const amenities: string[] = Array.isArray(offer?.amenities) ? offer.amenities : [];
  const [step, setStep] = useState<ReviewStep>("review");

  // Confirmations
  const [confirmedItems, setConfirmedItems] = useState<Record<string, boolean>>({});

  // Counter form state
  const [counterPropertyType, setCounterPropertyType] = useState("");
  const [counterBedrooms, setCounterBedrooms] = useState(1);
  const [counterBathrooms, setCounterBathrooms] = useState(1);
  const [counterNights, setCounterNights] = useState(1);
  const [counterAmenities, setCounterAmenities] = useState<string[]>([]);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterMessage, setCounterMessage] = useState("");

  // Reset on open
  useEffect(() => {
    if (open && offer) {
      setStep("review");
      setConfirmedItems({});
      setCounterPropertyType(offer.property_type || "");
      setCounterBedrooms(offer.bedrooms || 1);
      setCounterBathrooms(offer.bathrooms || 1);
      setCounterNights(offer.nights || 1);
      setCounterAmenities(amenities);
      setCounterPrice(String(offer.max_price_per_night || ""));
      setCounterMessage("");
    }
  }, [open, offer?.id]);

  if (!offer) return null;

  const toggleConfirm = (key: string) => {
    setConfirmedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const requiredKeys = ["property_type", "bedrooms", "bathrooms", "nights", "amenities", "price"];
  const allConfirmed = requiredKeys.every((k) => confirmedItems[k]);
  const confirmedCount = requiredKeys.filter((k) => confirmedItems[k]).length;

  const handleAccept = () => {
    if (!allConfirmed) {
      return;
    }
    onAccept(notification);
  };

  const handleStartCounter = () => {
    setStep("counter");
  };

  const handleSendCounter = () => {
    if (!counterPrice || parseFloat(counterPrice) < 15000) {
      return;
    }
    onSendCounter(notification, {
      price: parseFloat(counterPrice),
      message: counterMessage,
      propertyType: counterPropertyType,
      bedrooms: counterBedrooms,
      bathrooms: counterBathrooms,
      nights: counterNights,
      amenities: counterAmenities,
    });
  };

  const toggleCounterAmenity = (a: string) => {
    setCounterAmenities((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]
    );
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-0">
        {/* Header with gradient */}
        <div className="sticky top-0 z-10 bg-gradient-to-b from-primary/[0.06] to-background px-5 pt-5 pb-3 border-b border-border/30">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {step === "review" ? "Revisão do Pedido" : "Contraproposta"}
            </DialogTitle>
            <DialogDescription>
              {step === "review"
                ? "Confirme cada requisito do hóspede antes de tomar uma decisão"
                : "Ajuste os detalhes que pretende oferecer ao hóspede"}
            </DialogDescription>
          </DialogHeader>

          {/* Guest info bar */}
          <div className="flex items-center gap-3 mt-3">
            {offer.guest?.avatar_url ? (
              <img src={offer.guest.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                {(offer.guest?.full_name || "H").charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{offer.guest?.full_name || "Hóspede"}</p>
              <p className="text-[11px] text-muted-foreground">
                {offer.city || offer.province} · {format(new Date(offer.check_in), "dd/MM")} – {format(new Date(offer.check_out), "dd/MM")}
              </p>
            </div>
            {step === "review" && (
              <div className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                allConfirmed ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              }`}>
                {confirmedCount}/{requiredKeys.length}
              </div>
            )}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === "review" ? (
            <motion.div
              key="review"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="px-5 pb-5 pt-3 space-y-2.5"
            >
              {/* Property type */}
              <ReviewItem
                icon={<HomeTypeIcon />}
                label={tAuto("auto.pages_explorar.133", "Tipo de espaço")}
                guestValue={PROPERTY_LABELS[offer.property_type] || offer.property_type}
                confirmed={!!confirmedItems.property_type}
                onToggle={() => toggleConfirm("property_type")}
              />

              {/* Bedrooms */}
              <ReviewItem
                icon={<BedIcon />}
                label={tAuto("auto.pages_explorar.134", "Número de quartos")}
                guestValue={`${offer.bedrooms} quarto${offer.bedrooms !== 1 ? "s" : ""}`}
                confirmed={!!confirmedItems.bedrooms}
                onToggle={() => toggleConfirm("bedrooms")}
              />

              {/* Bathrooms */}
              <ReviewItem
                icon={<BathIcon />}
                label={tAuto("auto.pages_explorar.135", "Casas de banho")}
                guestValue={`${offer.bathrooms} WC`}
                confirmed={!!confirmedItems.bathrooms}
                onToggle={() => toggleConfirm("bathrooms")}
              />

              {/* Nights */}
              <ReviewItem
                icon={<MoonIcon />}
                label={tAuto("auto.pages_explorar.136", "Número de noites")}
                guestValue={`${offer.nights || "—"} noite${(offer.nights || 0) !== 1 ? "s" : ""}`}
                confirmed={!!confirmedItems.nights}
                onToggle={() => toggleConfirm("nights")}
              />

              {/* Amenities */}
              <ReviewItem
                icon={
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                }
                label="Comodidades desejadas"
                guestValue={
                  amenities.length > 0 ? (
                    <span className="flex flex-wrap gap-1 mt-0.5">
                      {amenities.map((a) => {
                        const Icon = getAmenityIcon(a);
                        return (
                          <span key={a} className="inline-flex items-center gap-0.5 text-[10px] bg-muted/50 rounded-md px-1.5 py-0.5">
                            <Icon className="w-3 h-3" />
                            {getAmenityLabel(a)}
                          </span>
                        );
                      })}
                    </span>
                  ) : (
                    "Nenhuma especificada"
                  )
                }
                confirmed={!!confirmedItems.amenities}
                onToggle={() => toggleConfirm("amenities")}
              />

              {/* Price */}
              <ReviewItem
                icon={<PriceTagIcon />}
                label={tAuto("auto.pages_explorar.137", "Preço por noite")}
                guestValue={
                  <span className="text-zinc-950 dark:text-zinc-50 font-black">
                    {fmt(offer.max_price_per_night)}/noite
                    {offer.nights && (
                      <span className="text-muted-foreground font-normal text-[11px] ml-2">
                        (Total: {fmt(offer.max_price_per_night * offer.nights)})
                      </span>
                    )}
                  </span>
                }
                confirmed={!!confirmedItems.price}
                onToggle={() => toggleConfirm("price")}
              />

              {/* Guest notes */}
              {offer.notes && (
                <div className="p-3 rounded-xl bg-muted/30 border border-border/30 mt-1">
                  <p className="text-[11px] font-medium text-muted-foreground mb-1">{tAuto("auto.pages_explorar.138", "Notas do hóspede")}</p>
                  <p className="text-xs text-foreground italic">"{offer.notes}"</p>
                </div>
              )}

              {/* Progress indicator */}
              <div className="pt-2">
                <div className="h-1 rounded-full bg-muted overflow-hidden">
                  <motion.div
                    className="h-full bg-primary rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${(confirmedCount / requiredKeys.length) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5 text-center">
                  {allConfirmed ? (
                    <span className="text-primary font-medium flex items-center justify-center gap-1">
                      <CircleCheck className="w-3.5 h-3.5" />
                      Todos os requisitos revistos
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-1">
                      <CircleAlert className="w-3.5 h-3.5" />
                      Confirme {requiredKeys.length - confirmedCount} item{requiredKeys.length - confirmedCount !== 1 ? "ns" : ""} restante{requiredKeys.length - confirmedCount !== 1 ? "s" : ""}
                    </span>
                  )}
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2.5 pt-3 border-t border-border/30">
                <Button
                  onClick={handleAccept}
                  disabled={!allConfirmed || isSubmitting}
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold shadow-sm disabled:opacity-40"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {isSubmitting ? "A processar..." : "Aceitar pedido"}
                </Button>

                <div className="flex gap-2.5">
                  <button
                    onClick={() => { onReject(notification.id); onClose(); }}
                    className="flex-1 h-11 rounded-xl border border-border/60 text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <X className="w-4 h-4" />
                    Rejeitar
                  </button>
                  <button
                    onClick={handleStartCounter}
                    className="flex-1 h-11 rounded-xl border border-primary/30 text-sm font-medium text-primary hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <CounterIcon />
                    Contraproposta
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="counter"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="px-5 pb-5 pt-3 space-y-4"
            >
              {/* Back button */}
              <button
                onClick={() => setStep("review")}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                ← Voltar à revisão
              </button>

              {/* Type */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">{tAuto("auto.pages_explorar.139", "Tipo de espaço")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {PROPERTY_TYPES_LIST.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setCounterPropertyType(type)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        (PROPERTY_LABELS[counterPropertyType] || counterPropertyType) === type
                          ? "bg-primary text-white"
                          : "bg-muted/50 text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
                {(PROPERTY_LABELS[offer.property_type] || offer.property_type) !== (PROPERTY_LABELS[counterPropertyType] || counterPropertyType) && (
                  <p className="text-[10px] text-amber-600 mt-1">Hóspede pediu: {PROPERTY_LABELS[offer.property_type] || offer.property_type}</p>
                )}
              </div>

              {/* Bedrooms & Bathrooms */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-2 block">Quartos</label>
                  <NumberStepper value={counterBedrooms} onChange={setCounterBedrooms} />
                  {counterBedrooms !== offer.bedrooms && (
                    <p className="text-[10px] text-amber-600 mt-1">Pedido: {offer.bedrooms}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-2 block">{tAuto("auto.pages_explorar.140", "Casas de banho")}</label>
                  <NumberStepper value={counterBathrooms} onChange={setCounterBathrooms} />
                  {counterBathrooms !== offer.bathrooms && (
                    <p className="text-[10px] text-amber-600 mt-1">Pedido: {offer.bathrooms}</p>
                  )}
                </div>
              </div>

              {/* Nights */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Noites</label>
                <NumberStepper value={counterNights} onChange={setCounterNights} max={365} />
                {counterNights !== (offer.nights || 1) && (
                  <p className="text-[10px] text-amber-600 mt-1">Pedido: {offer.nights} noites</p>
                )}
              </div>

              {/* Amenities */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">{tAuto("auto.pages_explorar.141", "Comodidades disponíveis")}</label>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {ALL_AMENITIES.map((a) => {
                    const Icon = getAmenityIcon(a);
                    const isSelected = counterAmenities.includes(a);
                    const wasRequested = amenities.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => toggleCounterAmenity(a)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-all border ${
                          isSelected
                            ? "bg-primary/10 text-primary border-primary/30"
                            : wasRequested
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted"
                        }`}
                      >
                        <Icon className="w-3 h-3" />
                        {getAmenityLabel(a)}
                      </button>
                    );
                  })}
                </div>
                {amenities.some((a) => !counterAmenities.includes(a)) && (
                  <p className="text-[10px] text-amber-600 mt-1">
                    Algumas comodidades solicitadas não estão incluídas
                  </p>
                )}
              </div>

              {/* Price */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">{tAuto("auto.pages_explorar.142", "Preço por noite (Kz)")}</label>
                <Input
                  type="number"
                  value={counterPrice}
                  onChange={(e) => setCounterPrice(e.target.value)}
                  className="h-12 text-lg font-bold"
                  min={15000}
                />
                <div className="flex items-center justify-between mt-1">
                  {counterPrice && (
                    <p className="text-[11px] text-muted-foreground">
                      {parseFloat(counterPrice) > offer.max_price_per_night
                        ? `+${Math.round(((parseFloat(counterPrice) - offer.max_price_per_night) / offer.max_price_per_night) * 100)}% acima`
                        : parseFloat(counterPrice) < offer.max_price_per_night
                        ? `${Math.round(((offer.max_price_per_night - parseFloat(counterPrice)) / offer.max_price_per_night) * 100)}% abaixo`
                        : "Mesmo valor"}
                    </p>
                  )}
                  <p className="text-[10px] text-amber-600">Oferta: {fmt(offer.max_price_per_night)}</p>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">{tAuto("auto.pages_explorar.143", "Mensagem ao hóspede (opcional)")}</label>
                <Textarea
                  placeholder={tAuto("auto.pages_explorar.144", "Ex: O espaço disponível tem 2 quartos em vez de 3, mas inclui pequeno-almoço...")}
                  value={counterMessage}
                  onChange={(e) => setCounterMessage(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Send */}
              <Button
                onClick={handleSendCounter}
                disabled={!counterPrice || isSubmitting}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold"
              >
                {isSubmitting ? "A enviar..." : "Enviar Contraproposta"}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

export default Explorar;
